<template>
  <div class="hms-page">
    <Navigation />
    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back"><i class="mdi mdi-arrow-left"></i></a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-shield-cross me-2"></i>{{ $t('page.maternal_safety', 'Maternal Safety — Save Every Mother') }}</h4>
        </div>
        <button class="hms-btn hms-btn-blue" @click="reloadAll"><i class="mdi mdi-refresh"></i></button>
      </div>

      <!-- TABS -->
      <div class="hms-tab-row">
        <button class="hms-tab" :class="{ active: tab==='risk' }" @click="tab='risk'; loadRisk()">
          <i class="mdi mdi-alert-decagram"></i> High-Risk
          <span v-if="risk.OpenAlerts > 0" class="hms-pill hms-pill-danger ms-1">{{ risk.OpenAlerts }}</span>
        </button>
        <button class="hms-tab" :class="{ active: tab==='ews' }" @click="tab='ews'">
          <i class="mdi mdi-pulse"></i> Pre-eclampsia EWS
        </button>
        <button class="hms-tab" :class="{ active: tab==='pph' }" @click="tab='pph'; loadPph()">
          <i class="mdi mdi-water-alert"></i> PPH Bundle
          <span v-if="pphStats.active > 0" class="hms-pill hms-pill-danger ms-1">{{ pphStats.active }} active</span>
        </button>
        <button class="hms-tab" :class="{ active: tab==='pac' }" @click="tab='pac'; loadPac()">
          <i class="mdi mdi-shield-heart"></i> PAC
          <span v-if="pacStats.follow_up_overdue > 0" class="hms-pill hms-pill-warn ms-1">{{ pacStats.follow_up_overdue }}</span>
        </button>
        <button class="hms-tab" :class="{ active: tab==='cancer' }" @click="tab='cancer'; loadCancer()">
          <i class="mdi mdi-ribbon"></i> Cancer Screening
        </button>
        <button class="hms-tab" :class="{ active: tab==='adolescent' }" @click="tab='adolescent'; loadAdolescent()">
          <i class="mdi mdi-account-school"></i> Adolescent
          <span v-if="adolStats.statutory > 0" class="hms-pill hms-pill-danger ms-1">{{ adolStats.statutory }} statutory</span>
        </button>
        <button class="hms-tab" :class="{ active: tab==='pnc' }" @click="tab='pnc'; loadPnc()">
          <i class="mdi mdi-baby-face-outline"></i> PNC Schedule
          <span v-if="pncStats.overdue > 0" class="hms-pill hms-pill-warn ms-1">{{ pncStats.overdue }} overdue</span>
        </button>
        <button class="hms-tab" :class="{ active: tab==='pmtct' }" @click="tab='pmtct'; loadPmtct()">
          <i class="mdi mdi-virus"></i> PMTCT
          <span v-if="pmtctCascade.vl_overdue > 0" class="hms-pill hms-pill-warn ms-1">{{ pmtctCascade.vl_overdue }} VL</span>
        </button>
        <button class="hms-tab" :class="{ active: tab==='mpdsr' }" @click="tab='mpdsr'; loadMpdsr()">
          <i class="mdi mdi-clipboard-pulse-outline"></i> MPDSR
          <span v-if="mpdsrStats.overdue_notify + mpdsrStats.overdue_review > 0" class="hms-pill hms-pill-danger ms-1">{{ mpdsrStats.overdue_notify + mpdsrStats.overdue_review }} overdue</span>
        </button>
      </div>

      <!-- ============ HIGH-RISK ============ -->
      <div v-if="tab==='risk'">
        <div class="hms-stat-row">
          <div class="hms-stat hms-stat-critical"><div class="hms-stat-num">{{ risk.Stats.critical || 0 }}</div><div class="hms-stat-lbl">Critical</div></div>
          <div class="hms-stat hms-stat-high"><div class="hms-stat-num">{{ risk.Stats.high || 0 }}</div><div class="hms-stat-lbl">High</div></div>
          <div class="hms-stat hms-stat-moderate"><div class="hms-stat-num">{{ risk.Stats.moderate || 0 }}</div><div class="hms-stat-lbl">Moderate</div></div>
          <div class="hms-stat"><div class="hms-stat-num">{{ risk.Stats.low_count || 0 }}</div><div class="hms-stat-lbl">Low</div></div>
          <div class="hms-stat hms-stat-danger"><div class="hms-stat-num">{{ risk.OpenAlerts || 0 }}</div><div class="hms-stat-lbl">Open alerts</div></div>
        </div>

        <div class="d-flex gap-2 mb-2 align-items-center">
          <select v-model="riskFilter" class="hms-input" @change="loadRisk" style="max-width:200px">
            <option value="">All categories</option>
            <option value="critical">Critical only</option>
            <option value="high">High</option>
            <option value="moderate">Moderate</option>
            <option value="low">Low</option>
          </select>
          <div v-if="riskPatientFilter" class="hms-pill hms-pill-info" style="font-size:12px">
            <i class="mdi mdi-account-filter"></i> Filtered to patient #{{ riskPatientFilter }}
            <a href="javascript:void(0)" @click="clearRiskPatientFilter" style="margin-left:6px"><i class="mdi mdi-close"></i></a>
          </div>
        </div>

        <table class="hms-table">
          <thead><tr><th>Patient</th><th>Age</th><th>G/P</th><th>Weeks</th><th>Risk score</th><th>Factors</th><th>Recommended</th><th>Next visit</th><th></th></tr></thead>
          <tbody>
            <tr v-for="r in risk.Pregnancies" :key="r.anc_visit_id" :class="riskRowClass(r.risk_category)">
              <td>
                <strong>{{ r.patient_name }}</strong>
                <div class="hms-muted-sm">{{ r.patient_no }} · {{ r.phone || '—' }}</div>
              </td>
              <td>{{ r.age }}</td>
              <td>G{{ r.gravida || '?' }}P{{ r.para || '?' }}</td>
              <td>{{ r.gestational_weeks || '?' }}w</td>
              <td>
                <span class="hms-pill" :class="riskPill(r.risk_category)">{{ r.risk_category }}</span>
                <div class="hms-muted-sm">{{ r.risk_score }} pts</div>
              </td>
              <td>
                <div v-for="(f, i) in (r.risk_factors || []).slice(0, 4)" :key="i" class="hms-muted-sm">• {{ f.desc }}</div>
              </td>
              <td><span class="hms-pill hms-pill-info">{{ r.recommended_facility || '—' }}</span></td>
              <td class="hms-muted-sm">{{ formatDate(r.next_visit_date) }}</td>
              <td style="white-space:nowrap">
                <button class="hms-btn hms-btn-secondary hms-btn-sm" @click="recompute(r.anc_visit_id)" title="Recompute risk"><i class="mdi mdi-refresh"></i></button>
                <a class="hms-btn hms-btn-secondary hms-btn-sm ms-1" :href="'#/maternity?tab=anc&visit_id=' + r.anc_visit_id" title="Open ANC visit"><i class="mdi mdi-mother-heart"></i></a>
              </td>
            </tr>
            <tr v-if="!risk.Pregnancies || !risk.Pregnancies.length"><td colspan="9" class="text-center hms-pad hms-muted">No active pregnancies in this category.</td></tr>
          </tbody>
        </table>

        <!-- Open alerts strip -->
        <h4 class="hms-h4 mt-4"><i class="mdi mdi-bell-alert"></i> Open clinical alerts</h4>
        <div v-for="a in alerts" :key="a.alert_id" class="hms-alert-row" :class="alertSeverityClass(a.severity)">
          <div>
            <strong>{{ a.alert_type.replace(/_/g, ' ') }}</strong> · <span class="hms-pill" :class="alertSeverityPill(a.severity)">{{ a.severity }}</span>
            <div class="hms-muted-sm">{{ a.patient_name }} ({{ a.patient_no }}) · {{ a.triggered_value }}</div>
            <div class="hms-muted-sm">Triggered {{ formatDt(a.triggered_at) }}</div>
          </div>
          <div class="d-flex gap-2">
            <button v-if="!a.acknowledged_at" class="hms-btn hms-btn-blue hms-btn-sm" @click="ackAlert(a)">Acknowledge</button>
            <button class="hms-btn hms-btn-success hms-btn-sm" @click="resolveAlert(a)">Resolve</button>
          </div>
        </div>
        <div v-if="!alerts.length" class="hms-empty">No open alerts.</div>
      </div>

      <!-- ============ PRE-ECLAMPSIA EWS ============ -->
      <div v-if="tab==='ews'">
        <div class="hms-card">
          <div class="hms-card-head">📈 Record BP — Pre-eclampsia Early Warning</div>
          <div class="hms-card-body">
            <p class="hms-muted-sm">Every BP reading runs through WHO classification. Severe PE or eclampsia triggers MgSO4 protocol guidance.</p>
            <div class="row g-2">
              <div class="col-3"><label class="hms-label">Patient ID *</label><input v-model.number="bpForm.patient_id" type="number" class="hms-input" /></div>
              <div class="col-2"><label class="hms-label">Systolic *</label><input v-model.number="bpForm.bp_systolic" type="number" class="hms-input" placeholder="120" /></div>
              <div class="col-2"><label class="hms-label">Diastolic *</label><input v-model.number="bpForm.bp_diastolic" type="number" class="hms-input" placeholder="80" /></div>
              <div class="col-2"><label class="hms-label">Pulse</label><input v-model.number="bpForm.pulse" type="number" class="hms-input" /></div>
              <div class="col-3"><label class="hms-label">Proteinuria</label>
                <select v-model="bpForm.proteinuria" class="hms-input">
                  <option value="nil">nil</option><option value="trace">trace</option>
                  <option value="+">+</option><option value="++">++</option><option value="+++">+++</option>
                </select>
              </div>
            </div>
            <label class="hms-label mt-2">Severe features (tick any present)</label>
            <div class="hms-severe-flags">
              <label v-for="f in severeFeatureOptions" :key="f.code"><input type="checkbox" :value="f.code" v-model="bpForm.severe_features_list" /> {{ f.label }}</label>
            </div>
            <div class="row g-2 mt-2">
              <div class="col-3"><label><input type="checkbox" v-model="bpForm.convulsion" :true-value="1" :false-value="0" /> <strong>Convulsion observed (eclampsia)</strong></label></div>
              <div class="col-9"><label class="hms-label">Notes</label><input v-model="bpForm.notes" class="hms-input" /></div>
            </div>
            <button class="hms-btn hms-btn-danger mt-3" :disabled="!bpForm.patient_id || !bpForm.bp_systolic || saving" @click="recordBp">{{ saving?'Saving…':'Record BP + classify' }}</button>
          </div>
        </div>

        <!-- Last result -->
        <div v-if="lastBpResult" class="hms-card mt-3" :class="ewsResultCard(lastBpResult.classification)">
          <div class="hms-card-head">Last classification: <strong>{{ lastBpResult.classification.replace(/_/g,' ') }}</strong></div>
          <div class="hms-card-body">
            <p><strong>Recommendation:</strong> {{ lastBpResult.recommendation }}</p>
            <button v-if="lastBpResult.mgso4_protocol_suggested" class="hms-btn hms-btn-danger" @click="startMgso4(lastBpResult)"><i class="mdi mdi-iv-bag"></i> Start MgSO4 protocol</button>
          </div>
        </div>

        <!-- Active eclampsia / severe PE patients -->
        <h4 class="hms-h4 mt-4"><i class="mdi mdi-alert-octagon"></i> Active concerns from open alerts</h4>
        <div v-for="a in alerts.filter(a => ['pre_eclampsia_severe','eclampsia','pre_eclampsia_mild','pih_threshold'].includes(a.alert_type))" :key="a.alert_id" class="hms-alert-row" :class="alertSeverityClass(a.severity)">
          <div>
            <strong>{{ a.patient_name }}</strong> ({{ a.patient_no }})
            <span class="hms-pill ms-2" :class="alertSeverityPill(a.severity)">{{ a.alert_type.replace(/_/g,' ') }}</span>
            <div class="hms-muted-sm">{{ a.triggered_value }} · {{ formatDt(a.triggered_at) }}</div>
          </div>
          <div class="d-flex gap-2">
            <button v-if="!a.acknowledged_at" class="hms-btn hms-btn-blue hms-btn-sm" @click="ackAlert(a)">Acknowledge</button>
            <button class="hms-btn hms-btn-success hms-btn-sm" @click="resolveAlert(a)">Resolve</button>
          </div>
        </div>
      </div>

      <!-- ============ PPH BUNDLE ============ -->
      <div v-if="tab==='pph'">
        <div class="hms-stat-row">
          <div class="hms-stat hms-stat-danger"><div class="hms-stat-num">{{ pphStats.active || 0 }}</div><div class="hms-stat-lbl">Active PPH</div></div>
          <div class="hms-stat"><div class="hms-stat-num">{{ pphStats.total || 0 }}</div><div class="hms-stat-lbl">Total events</div></div>
          <div class="hms-stat hms-stat-warning"><div class="hms-stat-num">{{ pphStats.mtp_total || 0 }}</div><div class="hms-stat-lbl">MTP activations</div></div>
          <div class="hms-stat hms-stat-danger"><div class="hms-stat-num">{{ pphStats.deaths || 0 }}</div><div class="hms-stat-lbl">PPH-related deaths</div></div>
          <div class="hms-stat"><div class="hms-stat-num">{{ pphStats.avg_loss ? Math.round(pphStats.avg_loss) : 0 }}</div><div class="hms-stat-lbl">Avg loss (mL)</div></div>
        </div>

        <div class="d-flex gap-2 mb-2 align-items-end">
          <button class="hms-btn hms-btn-danger hms-btn-lg" @click="openNewPphModal"><i class="mdi mdi-water-alert"></i> Open NEW PPH event</button>
        </div>

        <div v-if="!activePph">
          <table class="hms-table">
            <thead><tr><th>Patient</th><th>Started</th><th>Elapsed</th><th>Cumulative loss</th><th>Cause</th><th>Status</th><th>MTP</th><th></th></tr></thead>
            <tbody>
              <tr v-for="e in pphEvents" :key="e.pph_id" :class="e.status==='active' ? 'row-action' : ''">
                <td>{{ e.patient_name }}<div class="hms-muted-sm">{{ e.patient_no }} · {{ e.blood_group }}</div></td>
                <td>{{ formatDt(e.started_at) }}<div class="hms-muted-sm">{{ e.timing.replace(/_/g, ' ') }}</div></td>
                <td>{{ e.elapsed_min }}m</td>
                <td><strong :class="lossClass(e.cumulative_loss_ml)">{{ e.cumulative_loss_ml }} mL</strong></td>
                <td class="hms-muted-sm">{{ e.suspected_cause }}</td>
                <td><span class="hms-pill" :class="pphStatusPill(e.status)">{{ e.status }}</span></td>
                <td><span v-if="e.massive_transfusion_protocol" class="hms-pill hms-pill-danger">MTP active</span></td>
                <td><button class="hms-btn hms-btn-blue hms-btn-sm" @click="openPphDetail(e)">Open</button></td>
              </tr>
              <tr v-if="!pphEvents.length"><td colspan="8" class="text-center hms-pad hms-muted">No PPH events.</td></tr>
            </tbody>
          </table>
        </div>

        <!-- ACTIVE PPH DETAIL -->
        <div v-if="activePph" class="hms-pph-detail">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h3 class="hms-h3">{{ activePph.Event.patient_name }} — PPH #{{ activePph.Event.pph_id }}</h3>
            <button class="hms-btn hms-btn-secondary" @click="activePph=null; loadPph()">← Back</button>
          </div>

          <!-- 4 T's banner -->
          <div class="hms-pph-banner" :class="'hms-pph-' + activePph.Event.status">
            <div class="hms-pph-loss">
              <div class="hms-pph-loss-num">{{ activePph.Event.cumulative_loss_ml }}</div>
              <div class="hms-pph-loss-lbl">mL cumulative loss</div>
            </div>
            <div>
              <div v-if="activePph.Event.cumulative_loss_ml >= 1500" class="hms-pph-banner-msg pt-bad"><strong>🚨 CRITICAL — Consider hysterectomy + ICU</strong></div>
              <div v-else-if="activePph.Event.cumulative_loss_ml >= 1000" class="hms-pph-banner-msg pt-bad"><strong>🚨 MTP ACTIVE — Massive Transfusion Protocol</strong></div>
              <div v-else-if="activePph.Event.cumulative_loss_ml >= 500"><strong>⚠ PPH definition reached — Bundle in action</strong></div>
              <div v-else>Bundle started — monitor & escalate</div>
              <div class="hms-muted-sm">Blood group: <strong>{{ activePph.Event.blood_group }}</strong> · Time since start: {{ pphElapsed }}m · {{ activePph.Event.timing.replace(/_/g,' ') }}</div>
            </div>
            <div v-if="!activePph.Event.amtsl_completed" class="hms-pph-amtsl-warn">
              <i class="mdi mdi-alert"></i> AMTSL not complete — was it given?
            </div>
          </div>

          <div class="hms-pph-grid">
            <!-- Left: blood loss tracker -->
            <div class="hms-card">
              <div class="hms-card-head">📉 Add blood loss</div>
              <div class="hms-card-body">
                <div class="row g-2">
                  <div class="col-4"><label class="hms-label">Added loss (mL) *</label><input v-model.number="lossForm.added_loss_ml" type="number" class="hms-input" /></div>
                  <div class="col-4"><label class="hms-label">BP</label>
                    <div class="d-flex gap-1">
                      <input v-model.number="lossForm.bp_systolic" type="number" class="hms-input" placeholder="sys" />
                      <input v-model.number="lossForm.bp_diastolic" type="number" class="hms-input" placeholder="dia" />
                    </div>
                  </div>
                  <div class="col-4"><label class="hms-label">Pulse</label><input v-model.number="lossForm.pulse" type="number" class="hms-input" /></div>
                  <div class="col-12"><label class="hms-label">Intervention just done</label><input v-model="lossForm.intervention_just_done" class="hms-input" placeholder="e.g. Oxytocin 10IU IM given, uterine massage" /></div>
                </div>
                <button class="hms-btn hms-btn-warning mt-2" :disabled="!lossForm.added_loss_ml || saving" @click="addPphLoss">Log + recompute</button>
              </div>

              <div class="hms-card-head" style="border-top:1px solid #e2e8f0">Loss timeline</div>
              <table class="hms-table">
                <thead><tr><th>Time</th><th>Added</th><th>Cumulative</th><th>BP/HR</th><th>Shock idx</th><th>Action</th></tr></thead>
                <tbody>
                  <tr v-for="l in activePph.LossLog" :key="l.log_id" :class="l.shock_index >= 1 ? 'row-warn' : ''">
                    <td class="hms-muted-sm">{{ formatDt(l.recorded_at) }}</td>
                    <td>{{ l.added_loss_ml }}mL</td>
                    <td><strong>{{ l.cumulative_ml }}mL</strong></td>
                    <td class="hms-muted-sm">{{ l.bp_systolic ? l.bp_systolic + '/' + l.bp_diastolic : '—' }} · {{ l.pulse || '' }}</td>
                    <td><span v-if="l.shock_index" :class="l.shock_index >= 1 ? 'pt-bad' : ''">{{ l.shock_index }}</span></td>
                    <td class="hms-muted-sm">{{ l.intervention_just_done }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Right: interventions checklist + close -->
            <div>
              <div class="hms-card">
                <div class="hms-card-head">🩺 Interventions checklist (4 T's)</div>
                <div class="hms-card-body">
                  <div class="hms-section-lbl">Tone (most common)</div>
                  <div class="hms-checks">
                    <label v-for="i in interventionGroups.tone" :key="i"><input type="checkbox" :value="i" v-model="interventionsList" /> {{ formatIntervention(i) }}</label>
                  </div>
                  <div class="hms-section-lbl mt-2">Trauma</div>
                  <div class="hms-checks">
                    <label v-for="i in interventionGroups.trauma" :key="i"><input type="checkbox" :value="i" v-model="interventionsList" /> {{ formatIntervention(i) }}</label>
                  </div>
                  <div class="hms-section-lbl mt-2">Tissue</div>
                  <div class="hms-checks">
                    <label v-for="i in interventionGroups.tissue" :key="i"><input type="checkbox" :value="i" v-model="interventionsList" /> {{ formatIntervention(i) }}</label>
                  </div>
                  <div class="hms-section-lbl mt-2">Thrombin / Transfusion</div>
                  <div class="hms-checks">
                    <label v-for="i in interventionGroups.thrombin" :key="i"><input type="checkbox" :value="i" v-model="interventionsList" /> {{ formatIntervention(i) }}</label>
                  </div>
                  <div class="hms-section-lbl mt-2">Surgical (escalation)</div>
                  <div class="hms-checks">
                    <label v-for="i in interventionGroups.surgical" :key="i"><input type="checkbox" :value="i" v-model="interventionsList" /> {{ formatIntervention(i) }}</label>
                  </div>

                  <div class="row g-2 mt-3">
                    <div class="col-4"><label class="hms-label">Blood units</label><input v-model.number="bloodForm.units_blood" type="number" class="hms-input" /></div>
                    <div class="col-4"><label class="hms-label">FFP units</label><input v-model.number="bloodForm.units_ffp" type="number" class="hms-input" /></div>
                    <div class="col-4"><label class="hms-label">Platelets</label><input v-model.number="bloodForm.units_platelets" type="number" class="hms-input" /></div>
                  </div>

                  <div class="hms-section-lbl mt-3">Team called</div>
                  <div class="hms-checks">
                    <label v-for="t in ['mo','obgyn','anaesthetist','surgeon','blood_bank','icu','operating_theatre']" :key="t"><input type="checkbox" :value="t" v-model="teamList" /> {{ t.replace(/_/g, ' ').toUpperCase() }}</label>
                  </div>

                  <button class="hms-btn hms-btn-blue mt-3" :disabled="saving" @click="saveInterventions">Save bundle progress</button>
                </div>
              </div>

              <div class="hms-card mt-3">
                <div class="hms-card-head">🏁 Close event</div>
                <div class="hms-card-body">
                  <div class="row g-2">
                    <div class="col-6"><label class="hms-label">Status</label>
                      <select v-model="closeForm.status" class="hms-input">
                        <option value="controlled">Controlled</option>
                        <option value="referred_out">Referred out</option>
                        <option value="maternal_death">Maternal death</option>
                      </select>
                    </div>
                    <div class="col-6"><label class="hms-label">Outcome</label>
                      <select v-model="closeForm.outcome" class="hms-input">
                        <option value="survived">Survived</option>
                        <option value="referred">Referred</option>
                        <option value="died">Died</option>
                      </select>
                    </div>
                    <div class="col-6"><label class="hms-label">Final blood loss (mL)</label><input v-model.number="closeForm.final_blood_loss_ml" type="number" class="hms-input" /></div>
                    <div class="col-6"><label class="hms-label">Final Hb</label><input v-model.number="closeForm.final_hb_level" type="number" step="0.1" class="hms-input" /></div>
                    <div class="col-12"><label class="hms-label">Notes</label><textarea v-model="closeForm.notes" class="hms-input" rows="2"></textarea></div>
                  </div>
                  <button class="hms-btn hms-btn-success mt-2" :disabled="saving" @click="closePph">Close PPH event</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- NEW PPH MODAL -->
      <div v-if="newPphModal" class="hms-modal-bg" @click.self="newPphModal=false">
        <div class="hms-modal" style="width:560px">
          <div class="hms-modal-head"><h5><i class="mdi mdi-water-alert"></i> Open NEW PPH event</h5><button class="hms-icon-btn" @click="newPphModal=false"><i class="mdi mdi-close"></i></button></div>
          <div class="hms-modal-body">
            <div class="row g-2">
              <div class="col-12"><label class="hms-label">Delivery ID *</label><input v-model.number="newPphForm.delivery_id" type="number" class="hms-input" placeholder="from /maternity" /></div>
              <div class="col-6"><label class="hms-label">Initial blood loss (mL) *</label><input v-model.number="newPphForm.initial_loss_ml" type="number" class="hms-input" /></div>
              <div class="col-6"><label class="hms-label">Timing</label>
                <select v-model="newPphForm.timing" class="hms-input">
                  <option value="intrapartum">Intrapartum</option>
                  <option value="immediate_pph">Immediate (within 4h)</option>
                  <option value="primary_4h">Primary (4-24h)</option>
                  <option value="primary_24h">Primary 24h</option>
                  <option value="secondary_pph">Secondary (>24h)</option>
                </select>
              </div>
              <div class="col-12"><label class="hms-label">Suspected cause (4 T's)</label>
                <select v-model="newPphForm.suspected_cause" class="hms-input">
                  <option value="tone">Tone (uterine atony) — most common</option>
                  <option value="trauma">Trauma (laceration / rupture)</option>
                  <option value="tissue">Tissue (retained products)</option>
                  <option value="thrombin">Thrombin (coagulopathy)</option>
                  <option value="unclear">Unclear / to be determined</option>
                </select>
              </div>
              <div class="col-12"><label class="hms-label">Cause detail</label><textarea v-model="newPphForm.cause_detail" class="hms-input" rows="2"></textarea></div>
            </div>
          </div>
          <div class="hms-modal-foot">
            <button class="hms-btn hms-btn-secondary" @click="newPphModal=false">Cancel</button>
            <button class="hms-btn hms-btn-danger" :disabled="!newPphForm.delivery_id || saving" @click="openNewPph">Open + start protocol</button>
          </div>
        </div>
      </div>

      <!-- ============ POST-ABORTION CARE (PAC) ============ -->
      <div v-if="tab==='pac'">
        <div class="hms-pac-notice">
          <i class="mdi mdi-shield-lock"></i>
          <div>
            <strong>Confidential register.</strong> Post-abortion care is a legal right in Kenya (where abortion law permits, and always for incomplete/septic abortion regardless of origin).
            We provide care without judgment. <strong>Family-planning offer is mandatory</strong> at discharge — single biggest factor preventing repeat unsafe abortion.
          </div>
        </div>

        <div class="hms-stat-row">
          <div class="hms-stat"><div class="hms-stat-num">{{ pacStats.total || 0 }}</div><div class="hms-stat-lbl">Total cases</div></div>
          <div class="hms-stat hms-stat-warning"><div class="hms-stat-num">{{ pacStats.severe || 0 }}</div><div class="hms-stat-lbl">Severe</div></div>
          <div class="hms-stat hms-stat-danger"><div class="hms-stat-num">{{ pacStats.unsafe || 0 }}</div><div class="hms-stat-lbl">Suspected unsafe</div></div>
          <div class="hms-stat hms-stat-success"><div class="hms-stat-num">{{ pacStats.fp_accepted || 0 }} / {{ pacStats.fp_offered || 0 }}</div><div class="hms-stat-lbl">FP accepted / offered</div></div>
          <div class="hms-stat hms-stat-warning"><div class="hms-stat-num">{{ pacStats.follow_up_overdue || 0 }}</div><div class="hms-stat-lbl">F/U overdue</div></div>
          <div class="hms-stat hms-stat-danger"><div class="hms-stat-num">{{ pacStats.deaths || 0 }}</div><div class="hms-stat-lbl">Deaths</div></div>
        </div>

        <div class="d-flex justify-content-end mb-2">
          <button class="hms-btn hms-btn-success" @click="openPacModal()"><i class="mdi mdi-plus"></i> New PAC case</button>
        </div>

        <table class="hms-table">
          <thead><tr><th>#</th><th>Patient</th><th>Age</th><th>Presented</th><th>Type</th><th>Severity</th><th>Method</th><th>FP</th><th>SGBV</th><th>Follow-up</th><th>Status</th><th></th></tr></thead>
          <tbody>
            <tr v-for="c in pacCases" :key="c.pac_id" :class="pacRowClass(c)">
              <td>#{{ c.pac_id }}</td>
              <td>{{ c.patient_name }}<div class="hms-muted-sm">{{ c.patient_no }}</div></td>
              <td>{{ c.age_at_presentation }}</td>
              <td>{{ formatDate(c.presentation_date) }}</td>
              <td><span class="hms-pill" :class="presentationPill(c.presentation)">{{ c.presentation.replace(/_/g, ' ') }}</span></td>
              <td><span class="hms-pill" :class="severityPill(c.severity)">{{ c.severity }}</span></td>
              <td>{{ c.method }}</td>
              <td>
                <span v-if="c.fp_method_accepted" class="hms-pill hms-pill-success">✓ {{ c.fp_method_chosen || 'accepted' }}</span>
                <span v-else-if="c.fp_method_offered" class="hms-pill hms-pill-warn">offered, declined</span>
                <span v-else class="hms-pill hms-pill-danger">NOT offered</span>
              </td>
              <td><span v-if="c.sgbv_concerns" class="hms-pill hms-pill-danger">⚠</span></td>
              <td>
                <span v-if="c.follow_up_completed" class="hms-pill hms-pill-success">done</span>
                <span v-else-if="c.follow_up_date && c.follow_up_date < new Date().toISOString().substring(0,10)" class="hms-pill hms-pill-danger">overdue</span>
                <span v-else-if="c.follow_up_date" class="hms-muted-sm">{{ formatDate(c.follow_up_date) }}</span>
                <span v-else class="hms-muted-sm">—</span>
              </td>
              <td><span class="hms-pill" :class="pacOutcomePill(c.outcome)">{{ c.outcome || 'in care' }}</span></td>
              <td>
                <button class="hms-btn hms-btn-secondary hms-btn-sm" @click="openPacModal(c)"><i class="mdi mdi-pencil"></i></button>
                <button v-if="!c.outcome && !c.discharge_date" class="hms-btn hms-btn-success hms-btn-sm" @click="dischargePac(c)">Discharge</button>
                <button v-if="c.follow_up_date && !c.follow_up_completed && c.discharge_date" class="hms-btn hms-btn-blue hms-btn-sm" @click="completeFollowup(c)">F/U done</button>
              </td>
            </tr>
            <tr v-if="!pacCases.length"><td colspan="12" class="text-center hms-pad hms-muted">No PAC cases recorded.</td></tr>
          </tbody>
        </table>
      </div>

      <!-- ============ CANCER SCREENING ============ -->
      <div v-if="tab==='cancer'">
        <div class="hms-stat-row">
          <div class="hms-stat"><div class="hms-stat-num">{{ cancerStats.total || 0 }}</div><div class="hms-stat-lbl">Total screens</div></div>
          <div class="hms-stat"><div class="hms-stat-num">{{ cancerStats.cervical || 0 }}</div><div class="hms-stat-lbl">Cervical</div></div>
          <div class="hms-stat"><div class="hms-stat-num">{{ cancerStats.breast || 0 }}</div><div class="hms-stat-lbl">Breast</div></div>
          <div class="hms-stat hms-stat-success"><div class="hms-stat-num">{{ cancerStats.negative || 0 }}</div><div class="hms-stat-lbl">Negative</div></div>
          <div class="hms-stat hms-stat-warning"><div class="hms-stat-num">{{ cancerStats.positive || 0 }}</div><div class="hms-stat-lbl">Positive</div></div>
          <div class="hms-stat hms-stat-danger"><div class="hms-stat-num">{{ cancerStats.cancers_confirmed || 0 }}</div><div class="hms-stat-lbl">Cancers confirmed</div></div>
        </div>

        <div class="d-flex justify-content-between mb-2">
          <button class="hms-btn hms-btn-success" @click="openCancerModal()"><i class="mdi mdi-plus"></i> Record screening</button>
          <button class="hms-btn hms-btn-secondary" @click="rebuildSchedule"><i class="mdi mdi-refresh-auto"></i> Rebuild schedule (all women)</button>
        </div>

        <!-- Due/overdue strip -->
        <h4 class="hms-h4 mt-2"><i class="mdi mdi-calendar-alert"></i> Due / overdue recalls</h4>
        <table class="hms-table">
          <thead><tr><th>Patient</th><th>Age</th><th>Type</th><th>Due</th><th>Days</th><th>Status</th><th>Reminders</th></tr></thead>
          <tbody>
            <tr v-for="d in cancerDue" :key="d.schedule_id" :class="d.status === 'overdue' ? 'row-action' : (d.days_to_due <= 7 ? 'row-warn' : '')">
              <td>{{ d.patient_name }}<div class="hms-muted-sm">{{ d.patient_no }} · {{ d.phone || '—' }}</div></td>
              <td>{{ d.age }}</td>
              <td><span class="hms-pill" :class="screenTypePill(d.screen_type)">{{ d.screen_type }}</span></td>
              <td>{{ formatDate(d.due_date) }}</td>
              <td>{{ d.days_to_due >= 0 ? d.days_to_due + 'd' : (Math.abs(d.days_to_due) + 'd overdue') }}</td>
              <td><span class="hms-pill" :class="d.status === 'overdue' ? 'hms-pill-danger' : 'hms-pill-warn'">{{ d.status }}</span></td>
              <td>{{ d.reminder_count }} sent</td>
            </tr>
            <tr v-if="!cancerDue.length"><td colspan="7" class="text-center hms-muted hms-pad">No pending recalls. Use "Rebuild schedule" to seed.</td></tr>
          </tbody>
        </table>

        <h4 class="hms-h4 mt-4"><i class="mdi mdi-clipboard-check"></i> Recent screenings</h4>
        <table class="hms-table">
          <thead><tr><th>Date</th><th>Patient</th><th>Age</th><th>Type</th><th>Result</th><th>Treatment</th><th>Next due</th><th>Referral</th></tr></thead>
          <tbody>
            <tr v-for="s in cancerScreenings" :key="s.screen_id" :class="cancerResultRow(s.result)">
              <td>{{ formatDate(s.screen_date) }}</td>
              <td>{{ s.patient_name }}<div class="hms-muted-sm">{{ s.patient_no }}</div></td>
              <td>{{ s.age_at_screen }}</td>
              <td><span class="hms-pill" :class="screenTypePill(s.screen_type)">{{ s.screen_type }}</span></td>
              <td><span class="hms-pill" :class="cancerResultPill(s.result)">{{ s.result.replace(/_/g, ' ') }}</span></td>
              <td>{{ s.treatment.replace(/_/g, ' ') }}</td>
              <td class="hms-muted-sm">{{ formatDate(s.next_screen_due) }}</td>
              <td>{{ s.referral_made ? s.referral_facility : '' }}</td>
            </tr>
            <tr v-if="!cancerScreenings.length"><td colspan="8" class="text-center hms-pad hms-muted">No screenings recorded.</td></tr>
          </tbody>
        </table>
      </div>

      <!-- PAC MODAL -->
      <div v-if="pacModal" class="hms-modal-bg" @click.self="pacModal=false">
        <div class="hms-modal" style="width:780px">
          <div class="hms-modal-head"><h5>{{ pacForm.pac_id ? 'Edit PAC case' : 'New PAC case' }}</h5><button class="hms-icon-btn" @click="pacModal=false"><i class="mdi mdi-close"></i></button></div>
          <div class="hms-modal-body">
            <div class="hms-pac-notice mb-3">
              <i class="mdi mdi-shield-lock"></i>
              <div>This record is <strong>confidential</strong>. Provide care without judgment.</div>
            </div>
            <div class="row g-2">
              <div class="col-4"><label class="hms-label">Patient ID *</label><input v-model.number="pacForm.patient_id" type="number" class="hms-input" /></div>
              <div class="col-4"><label class="hms-label">Gestation (weeks)</label><input v-model.number="pacForm.gestation_weeks" type="number" class="hms-input" /></div>
              <div class="col-4"><label class="hms-label">Presentation</label>
                <select v-model="pacForm.presentation" class="hms-input">
                  <option value="threatened">Threatened</option>
                  <option value="inevitable">Inevitable</option>
                  <option value="incomplete">Incomplete</option>
                  <option value="complete">Complete</option>
                  <option value="missed">Missed</option>
                  <option value="septic">Septic</option>
                  <option value="induced_unsafe">Suspected unsafe induced</option>
                </select>
              </div>
              <div class="col-4"><label class="hms-label">Severity</label>
                <select v-model="pacForm.severity" class="hms-input">
                  <option value="uncomplicated">Uncomplicated</option><option value="mild">Mild</option>
                  <option value="moderate">Moderate</option><option value="severe">Severe</option><option value="life_threatening">Life threatening</option>
                </select>
              </div>
              <div class="col-4"><label class="hms-label">Bleeding</label>
                <select v-model="pacForm.bleeding" class="hms-input">
                  <option value="none">None</option><option value="mild">Mild</option><option value="moderate">Moderate</option><option value="heavy">Heavy</option><option value="life_threatening">Life threatening</option>
                </select>
              </div>
              <div class="col-4"><label class="hms-label">Method</label>
                <select v-model="pacForm.method" class="hms-input">
                  <option value="expectant">Expectant</option>
                  <option value="medical_misoprostol">Medical (misoprostol)</option>
                  <option value="MVA">MVA</option>
                  <option value="D_and_C">D &amp; C</option>
                  <option value="laparotomy">Laparotomy</option>
                  <option value="antibiotics_only">Antibiotics only (sepsis)</option>
                </select>
              </div>
              <div class="col-3"><label class="hms-label">Temp</label><input v-model.number="pacForm.temperature" type="number" step="0.1" class="hms-input" /></div>
              <div class="col-3"><label class="hms-label">BP</label>
                <div class="d-flex gap-1">
                  <input v-model.number="pacForm.bp_systolic" type="number" class="hms-input" placeholder="sys" />
                  <input v-model.number="pacForm.bp_diastolic" type="number" class="hms-input" placeholder="dia" />
                </div>
              </div>
              <div class="col-3"><label class="hms-label">Pulse</label><input v-model.number="pacForm.pulse" type="number" class="hms-input" /></div>
              <div class="col-3"><label class="hms-label">Hb</label><input v-model.number="pacForm.hb_level" type="number" step="0.1" class="hms-input" /></div>
              <div class="col-12"><label class="hms-label">Complications</label><textarea v-model="pacForm.complications" class="hms-input" rows="2"></textarea></div>

              <div class="col-12 hms-section-lbl mt-2">🚨 Family Planning (required at discharge)</div>
              <div class="col-4"><label><input type="checkbox" v-model="pacForm.fp_method_offered" :true-value="1" :false-value="0" /> FP offered</label></div>
              <div class="col-4"><label><input type="checkbox" v-model="pacForm.fp_method_accepted" :true-value="1" :false-value="0" /> FP accepted</label></div>
              <div class="col-4"><label><input type="checkbox" v-model="pacForm.fp_started_same_visit" :true-value="1" :false-value="0" /> Started same visit</label></div>
              <div v-if="pacForm.fp_method_accepted" class="col-12"><label class="hms-label">Method chosen</label>
                <select v-model="pacForm.fp_method_chosen" class="hms-input">
                  <option value="">—</option>
                  <option>Copper IUCD (10-yr)</option><option>Hormonal IUS (5-yr)</option>
                  <option>Implant 3-year</option><option>Implant 5-year</option>
                  <option>DMPA injection</option><option>Sayana Press</option>
                  <option>NET-EN injection</option><option>Combined OCP</option>
                  <option>POP</option><option>Condom</option><option>EC pill</option><option>Awareness method</option>
                </select>
              </div>
              <div v-if="pacForm.fp_method_offered && !pacForm.fp_method_accepted" class="col-12"><label class="hms-label">Refusal reason</label><input v-model="pacForm.fp_refusal_reason" class="hms-input" /></div>

              <div class="col-12 hms-section-lbl mt-2">🛡️ SGBV + Mental health screening</div>
              <div class="col-3"><label><input type="checkbox" v-model="pacForm.sgbv_screened" :true-value="1" :false-value="0" /> SGBV screened</label></div>
              <div class="col-3"><label><input type="checkbox" v-model="pacForm.sgbv_concerns" :true-value="1" :false-value="0" /> SGBV concerns</label></div>
              <div class="col-3"><label><input type="checkbox" v-model="pacForm.mental_health_screened" :true-value="1" :false-value="0" /> MH screened</label></div>
              <div class="col-3"><label><input type="checkbox" v-model="pacForm.suicidal_ideation" :true-value="1" :false-value="0" /> Suicidal ideation</label></div>
              <div class="col-4"><label class="hms-label">EPDS score</label><input v-model.number="pacForm.epds_score" type="number" min="0" max="30" class="hms-input" /></div>
              <div class="col-4"><label><input type="checkbox" v-model="pacForm.mh_referral_made" :true-value="1" :false-value="0" /> MH referral made</label></div>
              <div class="col-4"><label><input type="checkbox" v-model="pacForm.counselling_received" :true-value="1" :false-value="0" /> Counselling received</label></div>
            </div>
          </div>
          <div class="hms-modal-foot">
            <button class="hms-btn hms-btn-secondary" @click="pacModal=false">Cancel</button>
            <button class="hms-btn hms-btn-success" :disabled="!pacForm.patient_id || saving" @click="savePac">{{ saving?'Saving…':'Save' }}</button>
          </div>
        </div>
      </div>

      <!-- CANCER SCREENING MODAL -->
      <div v-if="cancerModal" class="hms-modal-bg" @click.self="cancerModal=false">
        <div class="hms-modal" style="width:660px">
          <div class="hms-modal-head"><h5>Record cancer screening</h5><button class="hms-icon-btn" @click="cancerModal=false"><i class="mdi mdi-close"></i></button></div>
          <div class="hms-modal-body">
            <div class="row g-2">
              <div class="col-4"><label class="hms-label">Patient ID *</label><input v-model.number="cancerForm.patient_id" type="number" class="hms-input" /></div>
              <div class="col-4"><label class="hms-label">Type *</label>
                <select v-model="cancerForm.screen_type" class="hms-input">
                  <optgroup label="Cervical">
                    <option value="VIA">VIA</option><option value="VILI">VILI</option>
                    <option value="HPV">HPV test</option><option value="Pap">Pap smear</option><option value="colposcopy">Colposcopy</option>
                  </optgroup>
                  <optgroup label="Breast">
                    <option value="SBE">Self breast exam</option>
                    <option value="CBE">Clinical breast exam</option>
                    <option value="mammography">Mammography</option>
                    <option value="ultrasound_breast">Breast ultrasound</option>
                  </optgroup>
                  <option value="biopsy">Biopsy (any)</option>
                </select>
              </div>
              <div class="col-4"><label class="hms-label">Date *</label><input v-model="cancerForm.screen_date" type="date" class="hms-input" /></div>
              <div class="col-6"><label class="hms-label">Result</label>
                <select v-model="cancerForm.result" class="hms-input">
                  <option value="pending">Pending</option>
                  <option value="negative">Negative</option>
                  <option value="positive_low_grade">Positive — low grade</option>
                  <option value="positive_high_grade">Positive — high grade</option>
                  <option value="suspicious_cancer">Suspicious — possible cancer</option>
                  <option value="inconclusive">Inconclusive</option>
                  <option value="referred">Referred</option>
                </select>
              </div>
              <div class="col-6"><label class="hms-label">Treatment</label>
                <select v-model="cancerForm.treatment" class="hms-input">
                  <option value="none">None / observation</option>
                  <option value="cryotherapy">Cryotherapy</option>
                  <option value="thermal_ablation">Thermal ablation</option>
                  <option value="LEEP">LEEP</option>
                  <option value="excision">Excision</option>
                  <option value="colposcopy_booked">Colposcopy booked</option>
                  <option value="biopsy_taken">Biopsy taken</option>
                  <option value="referred_specialist">Referred to specialist</option>
                </select>
              </div>
              <div class="col-12"><label class="hms-label">Findings</label><textarea v-model="cancerForm.findings" class="hms-input" rows="2"></textarea></div>
              <div class="col-6"><label class="hms-label">Next screen due</label><input v-model="cancerForm.next_screen_due" type="date" class="hms-input" /><div class="hms-muted-sm">Auto-set if blank: VIA neg→5y, +ve→1y, high-grade→30d, mammo neg→1y</div></div>
              <div class="col-6"><label class="hms-label">Referral facility</label><input v-model="cancerForm.referral_facility" class="hms-input" /></div>
              <div class="col-12"><label class="hms-label">Notes</label><textarea v-model="cancerForm.notes" class="hms-input" rows="2"></textarea></div>
            </div>
          </div>
          <div class="hms-modal-foot">
            <button class="hms-btn hms-btn-secondary" @click="cancerModal=false">Cancel</button>
            <button class="hms-btn hms-btn-success" :disabled="!cancerForm.patient_id || !cancerForm.screen_date || saving" @click="saveCancerScreen">{{ saving?'Saving…':'Save' }}</button>
          </div>
        </div>
      </div>

      <!-- ============ ADOLESCENT PREGNANCY REGISTER ============ -->
      <div v-if="tab==='adolescent'">
        <div class="hms-confidential-banner">
          <i class="mdi mdi-shield-lock"></i>
          <strong>Confidential register</strong> — Adolescent reproductive health. Statutory concern triggers Kenya Sexual Offences Act mandatory reporting.
        </div>

        <div class="hms-stat-row">
          <div class="hms-stat"><div class="hms-stat-num">{{ adolStats.total || 0 }}</div><div class="hms-stat-lbl">Registered</div></div>
          <div class="hms-stat hms-stat-warning"><div class="hms-stat-num">{{ adolStats.ongoing || 0 }}</div><div class="hms-stat-lbl">Ongoing</div></div>
          <div class="hms-stat hms-stat-danger"><div class="hms-stat-num">{{ adolStats.statutory || 0 }}</div><div class="hms-stat-lbl">Statutory concern</div></div>
          <div class="hms-stat hms-stat-critical"><div class="hms-stat-num">{{ adolStats.under16 || 0 }}</div><div class="hms-stat-lbl">Under 16</div></div>
          <div class="hms-stat hms-stat-warning"><div class="hms-stat-num">{{ adolStats.dropouts || 0 }}</div><div class="hms-stat-lbl">School dropouts</div></div>
          <div class="hms-stat hms-stat-info"><div class="hms-stat-num">{{ adolStats.year1_due || 0 }}</div><div class="hms-stat-lbl">1-yr follow-up due</div></div>
        </div>

        <div class="d-flex gap-2 mb-2">
          <select v-model="adolFilter" class="hms-input" @change="loadAdolescent" style="max-width:230px">
            <option value="">All entries</option>
            <option value="ongoing">Ongoing pregnancies</option>
            <option value="delivered">Delivered</option>
            <option value="statutory">Statutory concern</option>
            <option value="school_dropout">School dropouts</option>
          </select>
          <button class="hms-btn hms-btn-blue" @click="openAdolModal()"><i class="mdi mdi-plus"></i> Add to register</button>
        </div>

        <table class="hms-table">
          <thead><tr><th>Patient</th><th>Age</th><th>Partner</th><th>Circumstances</th><th>School</th><th>Outcome</th><th>FP</th><th>Follow-up</th><th></th></tr></thead>
          <tbody>
            <tr v-for="a in adolRows" :key="a.apr_id" :class="adolRowClass(a)">
              <td>
                <strong>{{ a.first_name }} {{ a.last_name }}</strong>
                <div class="hms-muted-sm">{{ a.patient_phone || '—' }} · booked {{ formatDate(a.booking_date) }}</div>
              </td>
              <td>
                <strong>{{ a.age_at_booking }}</strong>
                <div v-if="a.partner_statutory_concern==1" class="hms-pill hms-pill-danger">STATUTORY</div>
              </td>
              <td>
                <span v-if="a.partner_age">{{ a.partner_age }} ({{ a.partner_age_difference > 0 ? '+' + a.partner_age_difference : a.partner_age_difference }})</span>
                <span v-else class="hms-muted-sm">—</span>
              </td>
              <td>
                <span class="hms-pill" :class="adolCircClass(a.pregnancy_circumstances)">{{ (a.pregnancy_circumstances || '').replace(/_/g,' ') }}</span>
                <div v-if="a.legal_referral_needed==1" class="hms-muted-sm">Legal: {{ a.legal_referral_made==1 ? '✓ made' : '⚠ pending' }}</div>
              </td>
              <td>
                <div v-if="a.in_school_before_pregnancy==1">
                  <div class="hms-muted-sm">{{ a.school_name || '—' }}</div>
                  <span class="hms-pill" :class="adolSchoolClass(a.school_reentry_status)">{{ (a.school_reentry_status || 'unknown').replace(/_/g,' ') }}</span>
                </div>
                <span v-else class="hms-muted-sm">Not in school</span>
              </td>
              <td>
                <span class="hms-pill" :class="adolOutcomeClass(a.pregnancy_outcome)">{{ (a.pregnancy_outcome || '').replace(/_/g,' ') }}</span>
                <div v-if="a.outcome_date" class="hms-muted-sm">{{ formatDate(a.outcome_date) }}</div>
              </td>
              <td>
                <span v-if="a.fp_post_delivery_accepted==1" class="hms-pill hms-pill-success">{{ a.fp_post_delivery_method || 'on FP' }}</span>
                <span v-else-if="a.fp_post_delivery_offered==1" class="hms-pill hms-pill-warn">offered</span>
                <span v-else class="hms-muted-sm">—</span>
              </td>
              <td>
                <span v-if="a.one_year_followup_done==1" class="hms-pill hms-pill-success">1y ✓</span>
                <span v-else-if="a.outcome_date" class="hms-muted-sm">due {{ formatDate(adolYearDue(a.outcome_date)) }}</span>
                <span v-else class="hms-muted-sm">—</span>
              </td>
              <td>
                <button class="hms-btn hms-btn-secondary hms-btn-sm" @click="editAdol(a)"><i class="mdi mdi-pencil"></i></button>
                <button v-if="a.outcome_date && !a.one_year_followup_done" class="hms-btn hms-btn-secondary hms-btn-sm ms-1" @click="markAdolFu(a, 'one_year')" title="Mark 1-yr follow-up done"><i class="mdi mdi-calendar-check"></i></button>
              </td>
            </tr>
            <tr v-if="!adolRows.length"><td colspan="9" class="text-center hms-pad hms-muted">No adolescent register entries.</td></tr>
          </tbody>
        </table>

        <!-- Adolescent modal -->
        <div v-if="adolModal" class="hms-modal-bg" @click.self="adolModal=false">
          <div class="hms-modal">
            <div class="hms-modal-head">
              <h5><i class="mdi mdi-account-school"></i> Adolescent register entry</h5>
              <button class="hms-btn hms-btn-icon" @click="adolModal=false"><i class="mdi mdi-close"></i></button>
            </div>
            <div class="hms-modal-body">
              <div class="hms-confidential-banner mb-3">Confidential — under Kenya Sexual Offences Act, patients under 16 require mandatory reporting.</div>
              <div class="hms-form-grid">
                <label>Patient ID <input type="number" v-model="adolForm.patient_id" class="hms-input" :disabled="!!adolForm.apr_id" /></label>
                <label>Booking date <input type="date" v-model="adolForm.booking_date" class="hms-input" /></label>
                <label>Age at booking <input type="number" v-model="adolForm.age_at_booking" class="hms-input" /></label>
                <label>Gestation (wks) <input type="number" v-model="adolForm.gestation_at_booking" class="hms-input" /></label>
              </div>

              <h6 class="mt-3"><i class="mdi mdi-shield-alert"></i> Circumstances</h6>
              <div class="hms-form-grid">
                <label>Circumstances
                  <select v-model="adolForm.pregnancy_circumstances" class="hms-input">
                    <option value="unclear">Unclear / not disclosed</option>
                    <option value="consensual_peer">Consensual with peer</option>
                    <option value="consensual_age_appropriate">Consensual age-appropriate</option>
                    <option value="sgbv">SGBV / sexual violence</option>
                    <option value="statutory_concern">Statutory concern</option>
                    <option value="declined_to_disclose">Declined to disclose</option>
                  </select>
                </label>
                <label>Partner age <input type="number" v-model="adolForm.partner_age" class="hms-input" /></label>
                <label class="hms-check">
                  <input type="checkbox" v-model="adolForm.legal_referral_needed" :true-value="1" :false-value="0" /> Legal referral needed
                </label>
                <label class="hms-check">
                  <input type="checkbox" v-model="adolForm.legal_referral_made" :true-value="1" :false-value="0" /> Legal referral made
                </label>
              </div>

              <h6 class="mt-3"><i class="mdi mdi-school"></i> Schooling</h6>
              <div class="hms-form-grid">
                <label class="hms-check">
                  <input type="checkbox" v-model="adolForm.in_school_before_pregnancy" :true-value="1" :false-value="0" /> In school before pregnancy
                </label>
                <label>School name <input v-model="adolForm.school_name" class="hms-input" /></label>
                <label>School level
                  <select v-model="adolForm.school_level" class="hms-input">
                    <option value="">—</option>
                    <option value="primary">Primary</option>
                    <option value="secondary">Secondary</option>
                    <option value="tertiary">Tertiary / University</option>
                    <option value="tvet">TVET</option>
                    <option value="none">Not in school</option>
                  </select>
                </label>
                <label>Form / class <input v-model="adolForm.school_form_class" class="hms-input" /></label>
              </div>

              <h6 class="mt-3"><i class="mdi mdi-account-supervisor"></i> Support system</h6>
              <div class="hms-form-grid">
                <label>Parental support
                  <select v-model="adolForm.parental_support_level" class="hms-input">
                    <option value="full">Full</option>
                    <option value="partial">Partial</option>
                    <option value="none">None</option>
                    <option value="antagonistic">Antagonistic / hostile</option>
                    <option value="unknown">Unknown</option>
                  </select>
                </label>
                <label>Primary caregiver <input v-model="adolForm.primary_caregiver" class="hms-input" /></label>
                <label>Caregiver phone <input v-model="adolForm.primary_caregiver_phone" class="hms-input" /></label>
                <label class="hms-check">
                  <input type="checkbox" v-model="adolForm.counselling_offered" :true-value="1" :false-value="0" /> Counselling offered
                </label>
                <label class="hms-check">
                  <input type="checkbox" v-model="adolForm.counselling_attended" :true-value="1" :false-value="0" /> Counselling attended
                </label>
                <label class="hms-check">
                  <input type="checkbox" v-model="adolForm.chv_assigned" :true-value="1" :false-value="0" /> CHV assigned
                </label>
                <label class="hms-check">
                  <input type="checkbox" v-model="adolForm.teen_friendly_clinic_assigned" :true-value="1" :false-value="0" /> Teen-friendly clinic
                </label>
              </div>

              <label class="d-block mt-3">Notes (confidential)
                <textarea v-model="adolForm.notes" rows="3" class="hms-input"></textarea>
              </label>

              <div v-if="adolForm.apr_id" class="mt-4 p-3" style="background:#f7fafc; border-radius:8px;">
                <h6><i class="mdi mdi-check-decagram"></i> Outcome & follow-up</h6>
                <div class="hms-form-grid">
                  <label>Pregnancy outcome
                    <select v-model="adolForm.pregnancy_outcome" class="hms-input">
                      <option value="ongoing">Ongoing</option>
                      <option value="delivered">Delivered</option>
                      <option value="miscarriage">Miscarriage</option>
                      <option value="induced_abortion">Induced abortion</option>
                      <option value="stillbirth">Stillbirth</option>
                      <option value="transferred">Transferred</option>
                      <option value="LTFU">Lost to follow-up</option>
                    </select>
                  </label>
                  <label>Outcome date <input type="date" v-model="adolForm.outcome_date" class="hms-input" /></label>
                  <label>School re-entry
                    <select v-model="adolForm.school_reentry_status" class="hms-input">
                      <option value="unknown">Unknown</option>
                      <option value="planning">Planning to return</option>
                      <option value="returned">Returned to school</option>
                      <option value="enrolled_alternative">Alternative learning</option>
                      <option value="dropped_out">Dropped out</option>
                      <option value="not_applicable">N/A</option>
                    </select>
                  </label>
                  <label>Re-entry date <input type="date" v-model="adolForm.school_reentry_date" class="hms-input" /></label>
                  <label class="hms-check">
                    <input type="checkbox" v-model="adolForm.fp_post_delivery_offered" :true-value="1" :false-value="0" /> FP offered post-delivery
                  </label>
                  <label class="hms-check">
                    <input type="checkbox" v-model="adolForm.fp_post_delivery_accepted" :true-value="1" :false-value="0" /> FP accepted
                  </label>
                  <label>FP method <input v-model="adolForm.fp_post_delivery_method" class="hms-input" /></label>
                </div>
              </div>
            </div>
            <div class="hms-modal-foot">
              <button class="hms-btn hms-btn-secondary" @click="adolModal=false">Cancel</button>
              <button class="hms-btn hms-btn-success" :disabled="!adolForm.patient_id || !adolForm.age_at_booking || saving" @click="saveAdol">{{ saving ? 'Saving…' : 'Save' }}</button>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ POSTNATAL CARE (PNC) ============ -->
      <div v-if="tab==='pnc'">
        <div class="hms-stat-row">
          <div class="hms-stat hms-stat-warning"><div class="hms-stat-num">{{ pncStats.due_today || 0 }}</div><div class="hms-stat-lbl">Due today</div></div>
          <div class="hms-stat hms-stat-danger"><div class="hms-stat-num">{{ pncStats.overdue || 0 }}</div><div class="hms-stat-lbl">Overdue</div></div>
          <div class="hms-stat hms-stat-success"><div class="hms-stat-num">{{ pncStats.completed || 0 }}</div><div class="hms-stat-lbl">Completed</div></div>
          <div class="hms-stat hms-stat-warning"><div class="hms-stat-num">{{ pncStats.missed || 0 }}</div><div class="hms-stat-lbl">Missed</div></div>
          <div class="hms-stat hms-stat-info"><div class="hms-stat-num">{{ pncStats.epds_due_week || 0 }}</div><div class="hms-stat-lbl">6-week EPDS due (7d)</div></div>
        </div>

        <div class="d-flex gap-2 mb-2">
          <select v-model="pncFilter" class="hms-input" @change="loadPnc" style="max-width:200px">
            <option value="">All visits</option>
            <option value="due_today">Due today</option>
            <option value="overdue">Overdue</option>
            <option value="upcoming">Upcoming (7d)</option>
            <option value="completed">Completed</option>
            <option value="missed">Missed</option>
          </select>
          <input type="number" v-model="pncScheduleId" placeholder="delivery_id" class="hms-input" style="max-width:140px" />
          <button class="hms-btn hms-btn-blue" :disabled="!pncScheduleId" @click="autoSchedule"><i class="mdi mdi-calendar-plus"></i> Auto-schedule 5 visits</button>
        </div>

        <table class="hms-table">
          <thead><tr><th>Patient</th><th>Delivery</th><th>Visit</th><th>Scheduled</th><th>Status</th><th>BP</th><th>EPDS</th><th>FP</th><th></th></tr></thead>
          <tbody>
            <tr v-for="v in pncRows" :key="v.pnc_id" :class="pncRowClass(v)">
              <td>
                <strong>{{ v.first_name }} {{ v.last_name }}</strong>
                <div class="hms-muted-sm">{{ v.patient_phone || '—' }}</div>
              </td>
              <td class="hms-muted-sm">
                {{ formatDate(v.delivered_at) }} · {{ v.delivery_mode || '—' }}
                <div>baby: {{ (v.baby_outcome || '').replace(/_/g,' ') }}</div>
              </td>
              <td><span class="hms-pill hms-pill-info">{{ pncTimingLabel(v.visit_timing) }}</span></td>
              <td class="hms-muted-sm">{{ formatDateTime(v.scheduled_date) }}</td>
              <td>
                <span class="hms-pill" :class="pncStatusClass(v.visit_status)">{{ v.visit_status }}</span>
              </td>
              <td>
                <span v-if="v.bp_systolic">{{ v.bp_systolic }}/{{ v.bp_diastolic }}</span>
                <span v-else class="hms-muted-sm">—</span>
              </td>
              <td>
                <span v-if="v.epds_score !== null && v.epds_score !== undefined" class="hms-pill" :class="epdsPill(v.epds_score, v.epds_q10_self_harm)">{{ v.epds_score }}</span>
                <span v-else class="hms-muted-sm">—</span>
              </td>
              <td>
                <span v-if="v.fp_started_today==1" class="hms-pill hms-pill-success">{{ v.fp_method_chosen || 'started' }}</span>
                <span v-else-if="v.fp_discussed==1" class="hms-pill hms-pill-warn">discussed</span>
                <span v-else class="hms-muted-sm">—</span>
              </td>
              <td>
                <button v-if="v.visit_status==='scheduled' || v.visit_status==='missed'" class="hms-btn hms-btn-success hms-btn-sm" @click="openPncVisit(v)"><i class="mdi mdi-clipboard-check"></i> Record</button>
                <button v-if="v.visit_status==='scheduled'" class="hms-btn hms-btn-secondary hms-btn-sm ms-1" @click="markMissed(v)" title="Mark missed"><i class="mdi mdi-close-circle"></i></button>
              </td>
            </tr>
            <tr v-if="!pncRows.length"><td colspan="9" class="text-center hms-pad hms-muted">No PNC visits.</td></tr>
          </tbody>
        </table>

        <!-- PNC visit modal -->
        <div v-if="pncModal" class="hms-modal-bg" @click.self="pncModal=false">
          <div class="hms-modal">
            <div class="hms-modal-head">
              <h5><i class="mdi mdi-clipboard-check"></i> PNC visit — {{ pncTimingLabel(pncForm.visit_timing) }}</h5>
              <button class="hms-btn hms-btn-icon" @click="pncModal=false"><i class="mdi mdi-close"></i></button>
            </div>
            <div class="hms-modal-body">
              <h6><i class="mdi mdi-account-heart"></i> Maternal vitals + exam</h6>
              <div class="hms-form-grid">
                <label>Visit date <input type="datetime-local" v-model="pncForm.visit_date" class="hms-input" /></label>
                <label>Location
                  <select v-model="pncForm.location" class="hms-input">
                    <option value="facility">Facility</option>
                    <option value="home_visit">Home visit</option>
                    <option value="community">Community</option>
                    <option value="phone">Phone</option>
                  </select>
                </label>
                <label>BP systolic <input type="number" v-model="pncForm.bp_systolic" class="hms-input" /></label>
                <label>BP diastolic <input type="number" v-model="pncForm.bp_diastolic" class="hms-input" /></label>
                <label>Pulse <input type="number" v-model="pncForm.pulse" class="hms-input" /></label>
                <label>Temp (°C) <input type="number" step="0.1" v-model="pncForm.temperature" class="hms-input" /></label>
                <label>Uterine involution
                  <select v-model="pncForm.uterine_involution" class="hms-input">
                    <option value="normal">Normal</option>
                    <option value="sub_involution">Sub-involution</option>
                    <option value="tender">Tender</option>
                    <option value="enlarged">Enlarged</option>
                    <option value="not_assessed">Not assessed</option>
                  </select>
                </label>
                <label>Lochia
                  <select v-model="pncForm.lochia" class="hms-input">
                    <option value="normal">Normal</option>
                    <option value="heavy">Heavy</option>
                    <option value="foul_smelling">Foul-smelling</option>
                    <option value="prolonged">Prolonged</option>
                    <option value="none">None</option>
                    <option value="not_assessed">Not assessed</option>
                  </select>
                </label>
                <label>Perineum
                  <select v-model="pncForm.perineum_healing" class="hms-input">
                    <option value="normal">Normal</option>
                    <option value="infected">Infected</option>
                    <option value="dehiscence">Dehiscence</option>
                    <option value="not_applicable">N/A</option>
                    <option value="not_assessed">Not assessed</option>
                  </select>
                </label>
                <label>Breast
                  <select v-model="pncForm.breast_problems" class="hms-input">
                    <option value="none">No problems</option>
                    <option value="engorgement">Engorgement</option>
                    <option value="mastitis">Mastitis</option>
                    <option value="cracked_nipples">Cracked nipples</option>
                    <option value="abscess">Abscess</option>
                    <option value="other">Other</option>
                  </select>
                </label>
              </div>

              <h6 class="mt-3"><i class="mdi mdi-alert"></i> Maternal danger signs (screen at every visit)</h6>
              <div class="hms-chip-row">
                <label v-for="opt in maternalDangerOpts" :key="opt" class="hms-chip">
                  <input type="checkbox" :value="opt" v-model="pncMaternalDangerArr" /> {{ opt.replace(/_/g,' ') }}
                </label>
              </div>

              <div v-if="pncIs6w" class="mt-3 p-3" style="background:#fef3c7; border-left:4px solid #d97706; border-radius:6px;">
                <h6><i class="mdi mdi-brain"></i> EPDS — Edinburgh Postnatal Depression Scale (mandatory at 6w)</h6>
                <div class="hms-form-grid">
                  <label>EPDS total score (0-30) <input type="number" min="0" max="30" v-model="pncForm.epds_score" class="hms-input" /></label>
                  <label class="hms-check">
                    <input type="checkbox" v-model="pncForm.epds_q10_self_harm" :true-value="1" :false-value="0" /> Q10 self-harm thoughts positive
                  </label>
                  <label class="hms-check">
                    <input type="checkbox" v-model="pncForm.suicidal_ideation" :true-value="1" :false-value="0" /> Active suicidal ideation
                  </label>
                </div>
                <div class="hms-muted-sm mt-2">Score 0-9 = low | 10-12 = possible depression | ≥13 = likely depression. <strong>Any Q10 positive answer requires same-day referral.</strong></div>
              </div>

              <h6 class="mt-3"><i class="mdi mdi-baby-face-outline"></i> Baby check</h6>
              <div class="hms-form-grid">
                <label class="hms-check">
                  <input type="checkbox" v-model="pncForm.baby_examined" :true-value="1" :false-value="0" /> Baby examined
                </label>
                <label>Baby weight (g) <input type="number" v-model="pncForm.baby_weight_g" class="hms-input" /></label>
                <label>Baby temp (°C) <input type="number" step="0.1" v-model="pncForm.baby_temperature" class="hms-input" /></label>
                <label>Feeding
                  <select v-model="pncForm.baby_feeding" class="hms-input">
                    <option value="exclusive_bf">Exclusive BF</option>
                    <option value="mixed">Mixed</option>
                    <option value="formula_only">Formula only</option>
                    <option value="complementary">Complementary</option>
                    <option value="not_assessed">Not assessed</option>
                  </select>
                </label>
                <label class="hms-check">
                  <input type="checkbox" v-model="pncForm.baby_jaundice" :true-value="1" :false-value="0" /> Jaundice
                </label>
                <label class="hms-check">
                  <input type="checkbox" v-model="pncForm.baby_cord_normal" :true-value="1" :false-value="0" /> Cord normal
                </label>
                <label>Immunizations given <input v-model="pncForm.baby_immunizations_given" class="hms-input" /></label>
              </div>
              <h6 class="mt-2"><i class="mdi mdi-alert-octagon"></i> Baby danger signs</h6>
              <div class="hms-chip-row">
                <label v-for="opt in babyDangerOpts" :key="opt" class="hms-chip">
                  <input type="checkbox" :value="opt" v-model="pncBabyDangerArr" /> {{ opt.replace(/_/g,' ') }}
                </label>
              </div>

              <h6 class="mt-3"><i class="mdi mdi-pill"></i> Family planning</h6>
              <div class="hms-form-grid">
                <label class="hms-check">
                  <input type="checkbox" v-model="pncForm.fp_discussed" :true-value="1" :false-value="0" /> FP discussed
                </label>
                <label>Method chosen <input v-model="pncForm.fp_method_chosen" class="hms-input" /></label>
                <label class="hms-check">
                  <input type="checkbox" v-model="pncForm.fp_started_today" :true-value="1" :false-value="0" /> Started today
                </label>
              </div>

              <h6 class="mt-3"><i class="mdi mdi-arrow-decision"></i> Plan</h6>
              <div class="hms-form-grid">
                <label class="hms-check">
                  <input type="checkbox" v-model="pncForm.referral_made" :true-value="1" :false-value="0" /> Referral made
                </label>
                <label>Referral reason <input v-model="pncForm.referral_reason" class="hms-input" /></label>
              </div>
              <label class="d-block mt-2">Notes <textarea v-model="pncForm.notes" rows="2" class="hms-input"></textarea></label>
            </div>
            <div class="hms-modal-foot">
              <button class="hms-btn hms-btn-secondary" @click="pncModal=false">Cancel</button>
              <button class="hms-btn hms-btn-success" :disabled="saving" @click="savePnc">{{ saving ? 'Saving…' : 'Save visit' }}</button>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ PMTCT CASCADE ============ -->
      <div v-if="tab==='pmtct'">
        <div class="hms-confidential-banner">
          <i class="mdi mdi-shield-lock"></i>
          <strong>Confidential register</strong> — HIV+ mothers and HIV-exposed infants. PMTCT cascade per Kenya MoH Guidelines 2022 (Option B+).
        </div>

        <!-- CASCADE FUNNEL -->
        <div class="hms-cascade">
          <div class="hms-cascade-step">
            <div class="hms-cascade-num">{{ pmtctCascade.funnel.enrolled || 0 }}</div>
            <div class="hms-cascade-lbl">Enrolled</div>
          </div>
          <i class="mdi mdi-chevron-right hms-cascade-arrow"></i>
          <div class="hms-cascade-step">
            <div class="hms-cascade-num">{{ pmtctCascade.funnel.on_art || 0 }}</div>
            <div class="hms-cascade-lbl">On ART</div>
            <div class="hms-cascade-pct">{{ pct(pmtctCascade.funnel.on_art, pmtctCascade.funnel.enrolled) }}</div>
          </div>
          <i class="mdi mdi-chevron-right hms-cascade-arrow"></i>
          <div class="hms-cascade-step">
            <div class="hms-cascade-num">{{ pmtctCascade.funnel.vl_done || 0 }}</div>
            <div class="hms-cascade-lbl">VL done</div>
            <div class="hms-cascade-pct">{{ pct(pmtctCascade.funnel.vl_done, pmtctCascade.funnel.on_art) }}</div>
          </div>
          <i class="mdi mdi-chevron-right hms-cascade-arrow"></i>
          <div class="hms-cascade-step">
            <div class="hms-cascade-num">{{ pmtctCascade.funnel.suppressed || 0 }}</div>
            <div class="hms-cascade-lbl">Suppressed</div>
            <div class="hms-cascade-pct">{{ pct(pmtctCascade.funnel.suppressed, pmtctCascade.funnel.vl_done) }}</div>
          </div>
          <i class="mdi mdi-chevron-right hms-cascade-arrow"></i>
          <div class="hms-cascade-step">
            <div class="hms-cascade-num">{{ pmtctCascade.funnel.delivered || 0 }}</div>
            <div class="hms-cascade-lbl">Delivered</div>
          </div>
          <i class="mdi mdi-chevron-right hms-cascade-arrow"></i>
          <div class="hms-cascade-step">
            <div class="hms-cascade-num">{{ pmtctCascade.funnel.final_negative || 0 }}</div>
            <div class="hms-cascade-lbl">Infant HIV-neg @18m</div>
            <div class="hms-cascade-pct hms-cascade-pct-good">{{ pct(pmtctCascade.funnel.final_negative, pmtctCascade.funnel.infant_enrolled) }}</div>
          </div>
        </div>
        <div v-if="pmtctCascade.funnel.infant_positive > 0" class="hms-banner hms-banner-danger mb-3">
          <span><i class="mdi mdi-alert"></i> <strong>{{ pmtctCascade.funnel.infant_positive }}</strong> HIV+ infant(s) — vertical transmission has occurred this cohort. Review root cause.</span>
        </div>

        <div class="hms-stat-row">
          <div class="hms-stat hms-stat-warning"><div class="hms-stat-num">{{ pmtctCascade.vl_overdue || 0 }}</div><div class="hms-stat-lbl">VL samples overdue</div></div>
          <div class="hms-stat hms-stat-warning"><div class="hms-stat-num">{{ pmtctCascade.dbs_overdue || 0 }}</div><div class="hms-stat-lbl">DBS tests overdue</div></div>
          <div class="hms-stat hms-stat-info"><div class="hms-stat-num">{{ pmtctCascade.funnel.infant_enrolled || 0 }}</div><div class="hms-stat-lbl">HEI active</div></div>
          <div class="hms-stat hms-stat-danger"><div class="hms-stat-num">{{ pmtctCascade.funnel.ltfu || 0 }}</div><div class="hms-stat-lbl">LTFU</div></div>
        </div>

        <!-- Sub-tabs: Mothers / Infants -->
        <div class="hms-tab-row" style="margin-top:10px">
          <button class="hms-tab" :class="{ active: pmtctView==='mothers' }" @click="pmtctView='mothers'; loadPmtctMothers()">
            <i class="mdi mdi-mother-heart"></i> Mothers
          </button>
          <button class="hms-tab" :class="{ active: pmtctView==='infants' }" @click="pmtctView='infants'; loadHei()">
            <i class="mdi mdi-baby-face"></i> Exposed Infants (HEI)
          </button>
        </div>

        <!-- ===== Mothers list ===== -->
        <div v-if="pmtctView==='mothers'">
          <div class="d-flex gap-2 mb-2">
            <select v-model="pmtctStageFilter" class="hms-input" @change="loadPmtctMothers" style="max-width:230px">
              <option value="">All cascade stages</option>
              <option value="enrolled">Enrolled (not on ART)</option>
              <option value="art_started">On ART (VL pending)</option>
              <option value="vl_done">VL done</option>
              <option value="suppressed">Suppressed</option>
              <option value="delivered">Delivered</option>
              <option value="infant_enrolled">Infant in care</option>
              <option value="infant_final_negative">Final negative</option>
              <option value="infant_positive">Infant HIV+</option>
              <option value="LTFU">LTFU</option>
            </select>
            <button class="hms-btn hms-btn-blue" @click="openPmtctEnrollModal()"><i class="mdi mdi-plus"></i> Enrol HIV+ mother</button>
          </div>

          <table class="hms-table">
            <thead><tr><th>Mother</th><th>Dx</th><th>ART</th><th>Cotri</th><th>Partner</th><th>Latest VL</th><th>Next VL</th><th>Stage</th><th>HEI</th><th></th></tr></thead>
            <tbody>
              <tr v-for="m in pmtctMothers" :key="m.pmtct_id" :class="pmtctRowClass(m)">
                <td>
                  <strong>{{ m.first_name }} {{ m.last_name }}</strong>
                  <div class="hms-muted-sm">{{ m.patient_phone || '—' }} · enrolled {{ formatDate(m.enrolled_at) }}</div>
                </td>
                <td>
                  <span class="hms-muted-sm">{{ m.diagnosis_setting }}</span>
                  <div class="hms-muted-sm">{{ m.test_date }}</div>
                </td>
                <td>
                  <span v-if="m.art_started==1" class="hms-pill hms-pill-success">{{ m.art_regimen || 'on ART' }}</span>
                  <span v-else class="hms-pill hms-pill-danger">Not started</span>
                </td>
                <td>
                  <i v-if="m.cotrimoxazole_prophylaxis==1" class="mdi mdi-check-circle" style="color:#22543d"></i>
                  <i v-else class="mdi mdi-close-circle" style="color:#742a2a"></i>
                </td>
                <td>
                  <span class="hms-pill" :class="partnerPill(m.partner_status)">{{ m.partner_status }}</span>
                  <div v-if="m.partner_on_art==1" class="hms-muted-sm">on ART</div>
                </td>
                <td>
                  <span v-if="m.latest_vl !== null && m.latest_vl !== undefined" class="hms-pill" :class="vlPill(m.latest_vl)">{{ m.latest_vl }} c/ml</span>
                  <span v-else class="hms-muted-sm">—</span>
                </td>
                <td class="hms-muted-sm">{{ formatDate(m.next_vl_due) || '—' }}</td>
                <td><span class="hms-pill" :class="stagePill(m.cascade_stage)">{{ (m.cascade_stage || '').replace(/_/g,' ') }}</span></td>
                <td class="hms-muted-sm">{{ m.hei_count || 0 }}</td>
                <td><button class="hms-btn hms-btn-secondary hms-btn-sm" @click="openPmtctDetail(m)" title="View / record VL"><i class="mdi mdi-eye"></i></button></td>
              </tr>
              <tr v-if="!pmtctMothers.length"><td colspan="10" class="text-center hms-pad hms-muted">No PMTCT mothers registered yet.</td></tr>
            </tbody>
          </table>
        </div>

        <!-- ===== Infants (HEI) list ===== -->
        <div v-if="pmtctView==='infants'">
          <div class="d-flex gap-2 mb-2">
            <select v-model="heiStatusFilter" class="hms-input" @change="loadHei" style="max-width:220px">
              <option value="">All HEI</option>
              <option value="on_followup">On follow-up</option>
              <option value="final_negative">Final negative</option>
              <option value="positive">HIV+ infant</option>
              <option value="LTFU">LTFU</option>
              <option value="died">Died</option>
            </select>
          </div>

          <table class="hms-table">
            <thead><tr><th>Infant</th><th>Mother</th><th>Age</th><th>Risk</th><th>ARV</th><th>Cotri</th><th>Feeding</th><th>DBS schedule</th><th>Status</th><th></th></tr></thead>
            <tbody>
              <tr v-for="h in heiRows" :key="h.hei_id" :class="heiRowClass(h)">
                <td>
                  <strong>{{ h.infant_name || '(baby of ' + h.mother_first_name + ')' }}</strong>
                  <div class="hms-muted-sm">DOB {{ formatDate(h.infant_dob) }} · {{ h.infant_sex || '—' }} · {{ h.birth_weight_g || '—' }}g</div>
                </td>
                <td>
                  {{ h.mother_first_name }} {{ h.mother_last_name }}
                  <div class="hms-muted-sm">{{ h.mother_phone || '—' }}</div>
                </td>
                <td>{{ h.infant_age_weeks }}w</td>
                <td>
                  <span class="hms-pill" :class="h.risk_classification==='high_risk' ? 'hms-pill-danger' : 'hms-pill-info'">{{ h.risk_classification.replace(/_/g,' ') }}</span>
                </td>
                <td class="hms-muted-sm">{{ h.arv_regimen || '—' }}</td>
                <td>
                  <i v-if="h.cotri_start_date" class="mdi mdi-check-circle" style="color:#22543d"></i>
                  <i v-else class="mdi mdi-close-circle" style="color:#742a2a"></i>
                </td>
                <td class="hms-muted-sm">{{ (h.feeding_option || '').replace(/_/g,' ') }}</td>
                <td>
                  <div class="hms-muted-sm" style="font-family:monospace; font-size:11px">{{ h.dbs_summary || '—' }}</div>
                  <div v-if="h.next_dbs_due" class="hms-muted-sm">Next: {{ formatDate(h.next_dbs_due) }}</div>
                </td>
                <td><span class="hms-pill" :class="heiStatusPill(h.final_status)">{{ (h.final_status || '').replace(/_/g,' ') }}</span></td>
                <td><button class="hms-btn hms-btn-secondary hms-btn-sm" @click="openHeiDetail(h)" title="View / record DBS"><i class="mdi mdi-eye"></i></button></td>
              </tr>
              <tr v-if="!heiRows.length"><td colspan="10" class="text-center hms-pad hms-muted">No HIV-exposed infants registered.</td></tr>
            </tbody>
          </table>
        </div>

        <!-- PMTCT enrol modal -->
        <div v-if="pmtctEnrollModal" class="hms-modal-bg" @click.self="pmtctEnrollModal=false">
          <div class="hms-modal">
            <div class="hms-modal-head">
              <h5><i class="mdi mdi-virus"></i> Enrol HIV+ mother (PMTCT)</h5>
              <button class="hms-btn hms-btn-icon" @click="pmtctEnrollModal=false"><i class="mdi mdi-close"></i></button>
            </div>
            <div class="hms-modal-body">
              <div class="hms-confidential-banner mb-3">Confidential — HIV status is restricted-access data.</div>
              <h6><i class="mdi mdi-account"></i> Patient + diagnosis</h6>
              <div class="hms-form-grid">
                <label>Patient ID <input type="number" v-model="pmtctForm.patient_id" class="hms-input" /></label>
                <label>ANC visit ID <input type="number" v-model="pmtctForm.anc_visit_id" class="hms-input" /></label>
                <label>Diagnosis setting
                  <select v-model="pmtctForm.diagnosis_setting" class="hms-input">
                    <option value="this_pregnancy">This pregnancy</option>
                    <option value="known_before">Known before pregnancy</option>
                    <option value="intrapartum">Intrapartum</option>
                    <option value="postpartum">Postpartum</option>
                  </select>
                </label>
                <label>Test date <input type="date" v-model="pmtctForm.test_date" class="hms-input" /></label>
                <label>Test type
                  <select v-model="pmtctForm.test_type" class="hms-input">
                    <option value="determine">Determine (rapid)</option>
                    <option value="first_response">First Response (rapid)</option>
                    <option value="HIV_PCR">HIV PCR</option>
                    <option value="retest">Retest confirmation</option>
                    <option value="existing_dx">Already diagnosed</option>
                  </select>
                </label>
              </div>

              <h6 class="mt-3"><i class="mdi mdi-flask"></i> Baseline workup</h6>
              <div class="hms-form-grid">
                <label>Baseline CD4 <input type="number" v-model="pmtctForm.baseline_cd4" class="hms-input" /></label>
                <label>CD4 date <input type="date" v-model="pmtctForm.cd4_date" class="hms-input" /></label>
                <label>WHO clinical stage
                  <select v-model="pmtctForm.who_clinical_stage" class="hms-input">
                    <option value="">—</option><option value="1">1</option><option value="2">2</option><option value="3">3</option><option value="4">4</option>
                  </select>
                </label>
                <label>Baseline VL (copies/ml) <input type="number" v-model="pmtctForm.baseline_vl_copies" class="hms-input" /></label>
                <label class="hms-check">
                  <input type="checkbox" v-model="pmtctForm.tb_screened" :true-value="1" :false-value="0" /> TB screened
                </label>
                <label>TB result
                  <select v-model="pmtctForm.tb_screen_result" class="hms-input">
                    <option value="">—</option>
                    <option value="negative">Negative</option>
                    <option value="presumptive">Presumptive TB</option>
                    <option value="confirmed_tb">Confirmed TB</option>
                    <option value="on_tb_tx">On TB Rx</option>
                  </select>
                </label>
              </div>

              <h6 class="mt-3"><i class="mdi mdi-pill"></i> ART (Option B+ lifelong)</h6>
              <div class="hms-form-grid">
                <label class="hms-check">
                  <input type="checkbox" v-model="pmtctForm.art_started" :true-value="1" :false-value="0" /> ART started
                </label>
                <label>ART start date <input type="date" v-model="pmtctForm.art_start_date" class="hms-input" /></label>
                <label>Regimen <input v-model="pmtctForm.art_regimen" class="hms-input" placeholder="TDF/3TC/DTG (TLD)" /></label>
                <label>ART line
                  <select v-model="pmtctForm.art_line" class="hms-input">
                    <option value="first_line">First line</option>
                    <option value="second_line">Second line</option>
                    <option value="third_line">Third line</option>
                  </select>
                </label>
                <label class="hms-check">
                  <input type="checkbox" v-model="pmtctForm.cotrimoxazole_prophylaxis" :true-value="1" :false-value="0" /> Co-trimoxazole started
                </label>
              </div>

              <h6 class="mt-3"><i class="mdi mdi-account-multiple"></i> Partner / index testing</h6>
              <div class="hms-form-grid">
                <label class="hms-check">
                  <input type="checkbox" v-model="pmtctForm.partner_tested" :true-value="1" :false-value="0" /> Partner tested
                </label>
                <label>Partner status
                  <select v-model="pmtctForm.partner_status" class="hms-input">
                    <option value="unknown">Unknown</option>
                    <option value="positive">Positive</option>
                    <option value="negative">Negative</option>
                    <option value="declined">Declined</option>
                    <option value="not_disclosed">Not disclosed</option>
                  </select>
                </label>
                <label class="hms-check">
                  <input type="checkbox" v-model="pmtctForm.partner_on_art" :true-value="1" :false-value="0" /> Partner on ART
                </label>
              </div>

              <label class="d-block mt-3">Notes (confidential)
                <textarea v-model="pmtctForm.notes" rows="3" class="hms-input"></textarea>
              </label>
            </div>
            <div class="hms-modal-foot">
              <button class="hms-btn hms-btn-secondary" @click="pmtctEnrollModal=false">Cancel</button>
              <button class="hms-btn hms-btn-success" :disabled="!pmtctForm.patient_id || saving" @click="savePmtctEnrol">{{ saving ? 'Saving…' : 'Save & schedule VL' }}</button>
            </div>
          </div>
        </div>

        <!-- VL detail modal -->
        <div v-if="vlModal && activePmtct" class="hms-modal-bg" @click.self="vlModal=false">
          <div class="hms-modal" style="width:760px">
            <div class="hms-modal-head">
              <h5><i class="mdi mdi-flask-outline"></i> {{ activePmtct.first_name }} {{ activePmtct.last_name }} — VL schedule</h5>
              <button class="hms-btn hms-btn-icon" @click="vlModal=false"><i class="mdi mdi-close"></i></button>
            </div>
            <div class="hms-modal-body">
              <div class="hms-muted-sm mb-2">ART: {{ activePmtct.art_regimen || '—' }} · Started {{ activePmtct.art_start_date || '—' }}</div>
              <table class="hms-table">
                <thead><tr><th>Due</th><th>Timing</th><th>Status</th><th>Sample date</th><th>Copies/ml</th><th>Action</th><th></th></tr></thead>
                <tbody>
                  <tr v-for="v in vlSchedule" :key="v.vl_id" :class="v.result_status==='high_level' ? 'row-action' : (v.result_status==='low_level' ? 'row-warn' : '')">
                    <td>{{ formatDate(v.sample_due_date) }}</td>
                    <td>{{ (v.timing || '').replace(/_/g,' ') }}</td>
                    <td><span class="hms-pill" :class="vlStatusPill(v.result_status)">{{ v.result_status }}</span></td>
                    <td>{{ formatDate(v.sample_taken_date) || '—' }}</td>
                    <td>{{ v.vl_copies !== null ? v.vl_copies : '—' }}</td>
                    <td class="hms-muted-sm">{{ v.action_taken || '—' }}</td>
                    <td>
                      <button v-if="v.result_status==='pending'" class="hms-btn hms-btn-success hms-btn-sm" @click="openVlEntry(v)">Record</button>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div v-if="vlEntry.vl_id" class="mt-3 p-3" style="background:#f7fafc; border-radius:8px;">
                <h6>Record VL result</h6>
                <div class="hms-form-grid">
                  <label>Sample taken date <input type="date" v-model="vlEntry.sample_taken_date" class="hms-input" /></label>
                  <label>Result date <input type="date" v-model="vlEntry.result_date" class="hms-input" /></label>
                  <label>Copies/ml <input type="number" v-model="vlEntry.vl_copies" class="hms-input" /></label>
                </div>
                <label class="d-block mt-2">Action taken <input v-model="vlEntry.action_taken" class="hms-input" /></label>
                <div class="d-flex gap-3 mt-2">
                  <label class="hms-check"><input type="checkbox" v-model="vlEntry.enhanced_adherence_counselling" :true-value="1" :false-value="0" /> Enhanced adherence counselling</label>
                  <label class="hms-check"><input type="checkbox" v-model="vlEntry.regimen_switch_made" :true-value="1" :false-value="0" /> Regimen switch made</label>
                </div>
                <div class="mt-2 d-flex justify-content-end gap-2">
                  <button class="hms-btn hms-btn-secondary" @click="vlEntry={}">Cancel</button>
                  <button class="hms-btn hms-btn-success" @click="saveVlEntry">Save VL result</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- HEI / DBS detail modal -->
        <div v-if="heiModal && activeHei" class="hms-modal-bg" @click.self="heiModal=false">
          <div class="hms-modal" style="width:760px">
            <div class="hms-modal-head">
              <h5><i class="mdi mdi-baby-face"></i> Infant of {{ activeHei.mother_first_name }} {{ activeHei.mother_last_name }} — DBS schedule</h5>
              <button class="hms-btn hms-btn-icon" @click="heiModal=false"><i class="mdi mdi-close"></i></button>
            </div>
            <div class="hms-modal-body">
              <div class="hms-muted-sm mb-2">DOB {{ activeHei.infant_dob }} · {{ activeHei.risk_classification }} · ARV: {{ activeHei.arv_regimen }} · Feeding: {{ activeHei.feeding_option }}</div>
              <table class="hms-table">
                <thead><tr><th>Scheduled</th><th>Test</th><th>Status</th><th>Sample</th><th>Result</th><th></th></tr></thead>
                <tbody>
                  <tr v-for="t in heiTests" :key="t.dbs_id" :class="t.result==='positive' ? 'row-action' : (t.result==='negative' ? '' : '')">
                    <td>{{ formatDate(t.scheduled_date) }}</td>
                    <td><span class="hms-pill hms-pill-info">{{ t.test_type.replace(/_/g,' ') }}</span></td>
                    <td><span class="hms-pill" :class="dbsResultPill(t.result)">{{ t.result }}</span></td>
                    <td>{{ formatDate(t.sample_taken_date) || '—' }}</td>
                    <td>{{ formatDate(t.result_date) || '—' }}</td>
                    <td>
                      <button v-if="t.result==='pending'" class="hms-btn hms-btn-success hms-btn-sm" @click="openDbsEntry(t)">Record</button>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div v-if="dbsEntry.dbs_id" class="mt-3 p-3" style="background:#f7fafc; border-radius:8px;">
                <h6>Record DBS / EID result</h6>
                <div class="hms-form-grid">
                  <label>Sample taken date <input type="date" v-model="dbsEntry.sample_taken_date" class="hms-input" /></label>
                  <label>Result
                    <select v-model="dbsEntry.result" class="hms-input">
                      <option value="pending">Pending lab</option>
                      <option value="negative">Negative</option>
                      <option value="positive">Positive</option>
                      <option value="rejected">Sample rejected</option>
                      <option value="indeterminate">Indeterminate</option>
                    </select>
                  </label>
                  <label>Result date <input type="date" v-model="dbsEntry.result_date" class="hms-input" /></label>
                  <label class="hms-check">
                    <input type="checkbox" v-model="dbsEntry.result_disclosed_to_mother" :true-value="1" :false-value="0" /> Disclosed to mother
                  </label>
                </div>
                <label class="d-block mt-2">Notes <input v-model="dbsEntry.notes" class="hms-input" /></label>
                <div class="mt-2 d-flex justify-content-end gap-2">
                  <button class="hms-btn hms-btn-secondary" @click="dbsEntry={}">Cancel</button>
                  <button class="hms-btn hms-btn-success" @click="saveDbsEntry">Save result</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ MPDSR ============ -->
      <div v-if="tab==='mpdsr'">
        <div class="hms-stat-row">
          <div class="hms-stat hms-stat-danger"><div class="hms-stat-num">{{ mpdsrStats.overdue_notify || 0 }}</div><div class="hms-stat-lbl">Overdue notify (>48h)</div></div>
          <div class="hms-stat hms-stat-warning"><div class="hms-stat-num">{{ mpdsrStats.overdue_review || 0 }}</div><div class="hms-stat-lbl">Overdue review (>7d)</div></div>
          <div class="hms-stat hms-stat-warning"><div class="hms-stat-num">{{ mpdsrStats.overdue_closure || 0 }}</div><div class="hms-stat-lbl">Overdue closure (>90d)</div></div>
          <div class="hms-stat hms-stat-success"><div class="hms-stat-num">{{ mpdsrStats.closed_count || 0 }}</div><div class="hms-stat-lbl">Closed</div></div>
          <div class="hms-stat"><div class="hms-stat-num">{{ mpdsrStats.maternal_deaths || 0 }}</div><div class="hms-stat-lbl">Maternal</div></div>
          <div class="hms-stat"><div class="hms-stat-num">{{ mpdsrStats.perinatal_deaths || 0 }}</div><div class="hms-stat-lbl">Perinatal</div></div>
        </div>

        <div v-if="!activeCase">
          <table class="hms-table">
            <thead><tr><th>Patient</th><th>Death date</th><th>Type</th><th>Cause</th><th>Place</th><th>Status</th><th>Hrs to notify</th><th>Actions</th><th></th></tr></thead>
            <tbody>
              <tr v-for="c in mpdsrCases" :key="c.review_id" :class="mpdsrRowClass(c)">
                <td>{{ c.patient_name }}<div class="hms-muted-sm">{{ c.patient_no }} · age {{ c.age_at_death || '?' }}</div></td>
                <td>{{ formatDate(c.death_date) }}<div class="hms-muted-sm">{{ c.death_time }}</div></td>
                <td><span class="hms-pill" :class="deathTypePill(c.death_type)">{{ c.death_type }}</span></td>
                <td class="hms-muted-sm">{{ c.primary_cause_text || c.immediate_cause_text || '—' }}</td>
                <td class="hms-muted-sm">{{ c.place_of_death || '—' }}</td>
                <td><span class="hms-pill" :class="mpdsrStatusPill(c.status)">{{ c.status.replace(/_/g, ' ') }}</span></td>
                <td>{{ c.hours_to_notify }}h <span v-if="c.hours_to_notify > 48" class="hms-pill hms-pill-danger ms-1">overdue</span></td>
                <td>{{ c.action_closed }}/{{ c.action_total }}<span v-if="c.action_overdue > 0" class="hms-pill hms-pill-warn ms-1">{{ c.action_overdue }} overdue</span></td>
                <td><button class="hms-btn hms-btn-blue hms-btn-sm" @click="openCase(c)">Review</button></td>
              </tr>
              <tr v-if="!mpdsrCases.length"><td colspan="9" class="text-center hms-pad hms-muted">No MPDSR cases — every maternal/perinatal death will appear here automatically.</td></tr>
            </tbody>
          </table>
        </div>

        <!-- MPDSR Case Detail -->
        <div v-if="activeCase" class="hms-mpdsr-detail">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h3 class="hms-h3">
              {{ activeCase.Review.patient_name }} —
              <span class="hms-pill" :class="deathTypePill(activeCase.Review.death_type)">{{ activeCase.Review.death_type }}</span>
              · {{ formatDate(activeCase.Review.death_date) }}
              <span class="hms-pill" :class="mpdsrStatusPill(activeCase.Review.status)">{{ activeCase.Review.status.replace(/_/g, ' ') }}</span>
            </h3>
            <button class="hms-btn hms-btn-secondary" @click="activeCase=null; loadMpdsr()">← Back to list</button>
          </div>

          <div class="hms-mpdsr-grid">
            <!-- left: review form -->
            <div class="hms-card">
              <div class="hms-card-head">📋 MPDSR Review</div>
              <div class="hms-card-body">

                <!-- 48h notification banner -->
                <div v-if="activeCase.Review.status === 'pending_notification'" class="hms-banner hms-banner-danger">
                  <strong>Action required:</strong> MoH guidelines require notification within 48 hours of every maternal/perinatal death.
                  <button class="hms-btn hms-btn-danger hms-btn-sm ms-2" @click="notify">Mark as Notified to County</button>
                </div>

                <div class="row g-2">
                  <div class="col-6"><label class="hms-label">Death classification (ICD-MM)</label>
                    <select v-model="reviewForm.death_classification" class="hms-input">
                      <option value="">—</option>
                      <option value="direct">Direct obstetric (e.g. PPH, eclampsia)</option>
                      <option value="indirect">Indirect (e.g. HIV, malaria, cardiac)</option>
                      <option value="coincidental">Coincidental (e.g. RTA)</option>
                      <option value="unknown">Unknown</option>
                    </select>
                  </div>
                  <div class="col-6"><label class="hms-label">Primary cause ICD-10/11</label><input v-model="reviewForm.primary_cause_icd" class="hms-input" placeholder="e.g. O72.1" /></div>
                  <div class="col-12"><label class="hms-label">Primary cause (text)</label><input v-model="reviewForm.primary_cause_text" class="hms-input" placeholder="e.g. Postpartum haemorrhage — uterine atony" /></div>
                  <div class="col-12"><label class="hms-label">Contributing causes</label><textarea v-model="reviewForm.contributing_causes" class="hms-input" rows="2"></textarea></div>
                  <div class="col-6"><label class="hms-label">Place of death</label>
                    <select v-model="reviewForm.place_of_death_category" class="hms-input">
                      <option value="">—</option>
                      <option value="facility_ward">Facility — ward</option>
                      <option value="facility_theatre">Facility — theatre</option>
                      <option value="facility_ed">Facility — ED</option>
                      <option value="home">Home</option>
                      <option value="in_transit">In transit</option>
                      <option value="community">Community</option>
                    </select>
                  </div>
                  <div class="col-6"><label class="hms-label">Avoidability</label>
                    <select v-model="reviewForm.avoidability" class="hms-input">
                      <option value="">—</option>
                      <option value="avoidable">Avoidable</option>
                      <option value="probably_avoidable">Probably avoidable</option>
                      <option value="not_avoidable">Not avoidable</option>
                      <option value="undetermined">Undetermined</option>
                    </select>
                  </div>
                </div>

                <div class="hms-section-lbl mt-3">🚦 3-Delays Model (WHO/UNFPA)</div>
                <div class="hms-delay-block">
                  <label><input type="checkbox" v-model="reviewForm.delay_1_present" :true-value="1" :false-value="0" /> <strong>Delay 1</strong> — in deciding to seek care (patient/family level)</label>
                  <textarea v-if="reviewForm.delay_1_present" v-model="reviewForm.delay_1_detail" class="hms-input mt-1" rows="2" placeholder="Why was the decision to seek care delayed?"></textarea>
                </div>
                <div class="hms-delay-block">
                  <label><input type="checkbox" v-model="reviewForm.delay_2_present" :true-value="1" :false-value="0" /> <strong>Delay 2</strong> — in reaching care (transport/distance)</label>
                  <textarea v-if="reviewForm.delay_2_present" v-model="reviewForm.delay_2_detail" class="hms-input mt-1" rows="2" placeholder="Transport, distance, terrain, lack of ambulance?"></textarea>
                </div>
                <div class="hms-delay-block">
                  <label><input type="checkbox" v-model="reviewForm.delay_3_present" :true-value="1" :false-value="0" /> <strong>Delay 3</strong> — in receiving adequate care (facility level)</label>
                  <textarea v-if="reviewForm.delay_3_present" v-model="reviewForm.delay_3_detail" class="hms-input mt-1" rows="2" placeholder="Drug stockout, blood unavailable, staff absent, equipment broken?"></textarea>
                </div>

                <div class="hms-section-lbl mt-3">🔍 Avoidable factors</div>
                <label class="hms-label mt-2">Patient / family factors</label>
                <textarea v-model="reviewForm.avoidable_patient" class="hms-input" rows="2"></textarea>
                <label class="hms-label mt-2">Community factors</label>
                <textarea v-model="reviewForm.avoidable_community" class="hms-input" rows="2"></textarea>
                <label class="hms-label mt-2">Health system factors</label>
                <textarea v-model="reviewForm.avoidable_health_system" class="hms-input" rows="2"></textarea>
                <label class="hms-label mt-2">Provider / clinical factors</label>
                <textarea v-model="reviewForm.avoidable_provider" class="hms-input" rows="2"></textarea>

                <div class="hms-section-lbl mt-3">🗓 Review committee</div>
                <div class="row g-2">
                  <div class="col-6"><label class="hms-label">Committee meeting date</label><input v-model="reviewForm.review_committee_date" type="date" class="hms-input" /></div>
                  <div class="col-6"><label class="hms-label">Attendees</label><input v-model="reviewForm.review_attendees" class="hms-input" placeholder="MO, NO, Sister i/c, OB/GYN, Lab, Pharm" /></div>
                  <div class="col-12"><label class="hms-label">Minutes / key discussion</label><textarea v-model="reviewForm.review_minutes" class="hms-input" rows="4"></textarea></div>
                </div>

                <div class="text-end mt-3">
                  <button class="hms-btn hms-btn-secondary me-2" @click="saveReview(0)">Save draft</button>
                  <button class="hms-btn hms-btn-success" @click="saveReview(1)">✓ Mark review complete</button>
                </div>
              </div>
            </div>

            <!-- right: action items + verbal autopsy -->
            <div>
              <div class="hms-card">
                <div class="hms-card-head">
                  📌 Action items
                  <button class="hms-btn hms-btn-success hms-btn-sm float-end" @click="openActionModal(null)"><i class="mdi mdi-plus"></i> Add</button>
                </div>
                <div class="hms-card-body p-0">
                  <table class="hms-table">
                    <thead><tr><th>Description</th><th>Dept</th><th>Due</th><th>Status</th><th></th></tr></thead>
                    <tbody>
                      <tr v-for="a in activeCase.Actions" :key="a.action_id" :class="a.status === 'overdue' ? 'row-action' : ''">
                        <td>{{ a.description }}<div class="hms-muted-sm">{{ a.category.replace(/_/g, ' ') }}</div></td>
                        <td>{{ a.assigned_to_dept || '—' }}</td>
                        <td class="hms-muted-sm">{{ formatDate(a.due_date) }}</td>
                        <td><span class="hms-pill" :class="actionStatusPill(a.status)">{{ a.status }}</span></td>
                        <td>
                          <button v-if="a.status !== 'closed'" class="hms-btn hms-btn-success hms-btn-sm" @click="closeAction(a)"><i class="mdi mdi-check"></i></button>
                        </td>
                      </tr>
                      <tr v-if="!activeCase.Actions || !activeCase.Actions.length"><td colspan="5" class="hms-empty">No action items yet. Add at least one to close out the review.</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div class="hms-card mt-3">
                <div class="hms-card-head">🗣 Verbal autopsy (family interview)</div>
                <div class="hms-card-body">
                  <p class="hms-muted-sm">Required for community deaths or where care-seeking pattern unclear.</p>
                  <button class="hms-btn hms-btn-secondary" @click="openVaModal"><i class="mdi mdi-clipboard-text"></i> {{ activeCase.VerbalAutopsy ? 'Edit' : 'Start' }} verbal autopsy</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Action item modal -->
    <div v-if="actionModal" class="hms-modal-bg" @click.self="actionModal=false">
      <div class="hms-modal" style="width:560px">
        <div class="hms-modal-head"><h5>{{ actionForm.action_id ? 'Edit action' : 'New action item' }}</h5><button class="hms-icon-btn" @click="actionModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <label class="hms-label">Category</label>
          <select v-model="actionForm.category" class="hms-input">
            <option value="clinical_protocol">Clinical protocol</option>
            <option value="training">Training</option>
            <option value="equipment">Equipment</option>
            <option value="drug_availability">Drug availability</option>
            <option value="referral_pathway">Referral pathway</option>
            <option value="community_education">Community education</option>
            <option value="blood_bank">Blood bank</option>
            <option value="communication">Communication</option>
            <option value="policy">Policy</option>
            <option value="other">Other</option>
          </select>
          <label class="hms-label mt-2">Description *</label>
          <textarea v-model="actionForm.description" class="hms-input" rows="3" placeholder="e.g. PPH simulation training for maternity team"></textarea>
          <div class="row g-2 mt-2">
            <div class="col-6"><label class="hms-label">Assigned dept</label><input v-model="actionForm.assigned_to_dept" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Priority</label>
              <select v-model="actionForm.priority" class="hms-input">
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">Due date</label><input v-model="actionForm.due_date" type="date" class="hms-input" /></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="actionModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="!actionForm.description || saving" @click="saveAction">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>

    <!-- Verbal autopsy modal -->
    <div v-if="vaModal" class="hms-modal-bg" @click.self="vaModal=false">
      <div class="hms-modal" style="width:760px">
        <div class="hms-modal-head"><h5>Verbal autopsy — family interview</h5><button class="hms-icon-btn" @click="vaModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-6"><label class="hms-label">Interview date</label><input v-model="vaForm.interview_date" type="date" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Relationship to deceased</label><input v-model="vaForm.relationship_to_deceased" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Informant name</label><input v-model="vaForm.informant_name" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Informant phone</label><input v-model="vaForm.informant_phone" class="hms-input" /></div>
            <div class="col-12"><label><input type="checkbox" v-model="vaForm.family_consent_obtained" :true-value="1" :false-value="0" /> Family consent obtained</label></div>
          </div>

          <div class="hms-section-lbl mt-3">Pregnancy & care seeking</div>
          <div class="row g-2">
            <div class="col-6"><label><input type="checkbox" v-model="vaForm.care_sought_during_pregnancy" :true-value="1" :false-value="0" /> Care sought during pregnancy</label></div>
            <div class="col-6"><label class="hms-label">ANC visits</label><input v-model.number="vaForm.anc_visits_count" type="number" class="hms-input" /></div>
            <div class="col-6"><label><input type="checkbox" v-model="vaForm.danger_signs_recognised" :true-value="1" :false-value="0" /> Family recognized danger signs</label></div>
            <div class="col-6"><label><input type="checkbox" v-model="vaForm.danger_signs_taught_at_anc" :true-value="1" :false-value="0" /> Danger signs taught at ANC</label></div>
          </div>

          <div class="hms-section-lbl mt-3">Delay 1 — decision</div>
          <div class="row g-2">
            <div class="col-4"><label><input type="checkbox" v-model="vaForm.decision_to_seek_care_by_self" :true-value="1" :false-value="0" /> By patient</label></div>
            <div class="col-4"><label><input type="checkbox" v-model="vaForm.decision_to_seek_care_by_husband" :true-value="1" :false-value="0" /> By husband</label></div>
            <div class="col-4"><label><input type="checkbox" v-model="vaForm.decision_to_seek_care_by_in_laws" :true-value="1" :false-value="0" /> By in-laws</label></div>
            <div class="col-12"><label class="hms-label">Hours from symptom to decision</label><input v-model.number="vaForm.time_from_symptom_to_decision_hours" type="number" class="hms-input" /></div>
          </div>

          <div class="hms-section-lbl mt-3">Delay 2 — transport</div>
          <div class="row g-2">
            <div class="col-4"><label class="hms-label">Transport</label>
              <select v-model="vaForm.transport_used" class="hms-input">
                <option value="">—</option><option value="ambulance">Ambulance</option><option value="boda">Boda boda</option>
                <option value="car">Car</option><option value="foot">Foot</option><option value="none">None</option>
              </select>
            </div>
            <div class="col-4"><label class="hms-label">Time decision→facility (min)</label><input v-model.number="vaForm.time_from_decision_to_facility_min" type="number" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Distance (km)</label><input v-model.number="vaForm.distance_to_facility_km" type="number" step="0.1" class="hms-input" /></div>
          </div>

          <div class="hms-section-lbl mt-3">Delay 3 — facility care</div>
          <div class="row g-2">
            <div class="col-12"><label class="hms-label">First facility visited</label><input v-model="vaForm.first_facility_visited" class="hms-input" /></div>
            <div class="col-4"><label><input type="checkbox" v-model="vaForm.referred_to_higher_level" :true-value="1" :false-value="0" /> Referred up</label></div>
            <div class="col-4"><label><input type="checkbox" v-model="vaForm.blood_available" :true-value="1" :false-value="0" /> Blood available</label></div>
            <div class="col-4"><label><input type="checkbox" v-model="vaForm.skilled_provider_available" :true-value="1" :false-value="0" /> Skilled provider on-site</label></div>
            <div class="col-12"><label class="hms-label">Care received summary</label><textarea v-model="vaForm.care_received_at_facility" class="hms-input" rows="3"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="vaModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveVA">{{ saving?'Saving…':'Save VA' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "MaternalSafety",
  components: { Navigation },
  data() {
    return {
      tab: 'risk', saving: false,
      risk: { Pregnancies: [], Stats: {}, OpenAlerts: 0 },
      riskFilter: '', riskPatientFilter: '', alerts: [],
      mpdsrCases: [], mpdsrStats: {},
      activeCase: null,
      reviewForm: {},
      actionModal: false, actionForm: {},
      vaModal: false, vaForm: {},
      // EWS
      bpForm: { patient_id: null, bp_systolic: null, bp_diastolic: null, pulse: null, proteinuria: 'nil', severe_features_list: [], convulsion: 0, notes: '' },
      lastBpResult: null,
      // PPH
      pphStats: {}, pphEvents: [], activePph: null,
      newPphModal: false, newPphForm: { delivery_id: null, initial_loss_ml: 0, timing: 'immediate_pph', suspected_cause: 'tone', cause_detail: '' },
      lossForm: { added_loss_ml: null, bp_systolic: null, bp_diastolic: null, pulse: null, intervention_just_done: '' },
      interventionsList: [], teamList: [],
      bloodForm: { units_blood: 0, units_ffp: 0, units_platelets: 0 },
      closeForm: { status: 'controlled', outcome: 'survived', final_blood_loss_ml: 0, final_hb_level: null, notes: '' },
      // PAC
      pacStats: {}, pacCases: [],
      pacModal: false, pacForm: this.blankPacForm(),
      // Cancer
      cancerStats: {}, cancerScreenings: [], cancerDue: [],
      cancerModal: false, cancerForm: this.blankCancerForm(),
      // Adolescent
      adolStats: {}, adolRows: [], adolFilter: '',
      adolModal: false, adolForm: this.blankAdolForm(),
      // PNC
      pncStats: {}, pncRows: [], pncFilter: '', pncScheduleId: null,
      pncModal: false, pncForm: this.blankPncForm(),
      pncMaternalDangerArr: [], pncBabyDangerArr: [],
      maternalDangerOpts: ['heavy_bleeding','fever','headache','swelling','difficulty_breathing','convulsions','severe_pain','foul_lochia','breast_abscess','calf_pain','depression'],
      babyDangerOpts: ['not_feeding','convulsion','fast_breathing','severe_chest_indrawing','fever','hypothermia','jaundice','lethargy'],
      // PMTCT
      pmtctCascade: { funnel: {}, vl_overdue: 0, dbs_overdue: 0 },
      pmtctView: 'mothers', pmtctMothers: [], pmtctStageFilter: '',
      pmtctEnrollModal: false, pmtctForm: this.blankPmtctForm(),
      activePmtct: null, vlModal: false, vlSchedule: [], vlEntry: {},
      heiRows: [], heiStatusFilter: '',
      activeHei: null, heiModal: false, heiTests: [], dbsEntry: {}
    };
  },
  computed: {
    severeFeatureOptions() { return [
      { code: 'headache', label: 'Severe headache' },
      { code: 'vision_blur', label: 'Blurred vision' },
      { code: 'epigastric_pain', label: 'Epigastric / RUQ pain' },
      { code: 'vomiting', label: 'Vomiting' },
      { code: 'oliguria', label: 'Oliguria' },
      { code: 'hyperreflexia', label: 'Hyperreflexia' }
    ]; },
    interventionGroups() { return {
      tone:      ['uterine_massage','bimanual_compression','oxytocin_10IU_IV','oxytocin_20IU_in_NS','ergometrine_im','misoprostol_800_pr','misoprostol_400_sl'],
      trauma:    ['genital_tract_repair','exploration_uterus'],
      tissue:    ['manual_removal_placenta'],
      thrombin:  ['tranexamic_acid','blood_transfusion','plasma_transfusion','platelets_transfusion'],
      surgical:  ['intrauterine_balloon','condom_tamponade','b_lynch_suture','uterine_artery_ligation','iliac_artery_ligation','hysterectomy']
    }; },
    pphElapsed() { if (!this.activePph) return 0; return Math.floor((Date.now() - new Date((this.activePph.Event.started_at || '').replace(' ','T')).getTime()) / 60000); },
    pncIs6w() { return this.pncForm && this.pncForm.visit_timing === '6wk'; }
  },
  mounted() {
    const q = this.$route.query || {};
    const valid = ['risk','ews','pph','pac','cancer','adolescent','pnc','pmtct','mpdsr'];
    if (q.tab && valid.includes(q.tab)) this.tab = q.tab;
    if (q.delivery_id) this.pncScheduleId = q.delivery_id;
    if (q.patient_id) this.riskPatientFilter = q.patient_id;
    // Initial load for the chosen tab
    if (this.tab === 'risk') this.loadRisk();
    else if (this.tab === 'pph') this.loadPph();
    else if (this.tab === 'pac') this.loadPac();
    else if (this.tab === 'cancer') this.loadCancer();
    else if (this.tab === 'adolescent') this.loadAdolescent();
    else if (this.tab === 'pnc') this.loadPnc();
    else if (this.tab === 'pmtct') this.loadPmtct();
    else if (this.tab === 'mpdsr') this.loadMpdsr();
    else this.loadRisk();
  },
  methods: {
    async reloadAll() {
      if (this.tab === 'risk') await this.loadRisk();
      else if (this.tab === 'mpdsr') await this.loadMpdsr();
      else if (this.tab === 'pph') await this.loadPph();
      else if (this.tab === 'pac') await this.loadPac();
      else if (this.tab === 'cancer') await this.loadCancer();
      else if (this.tab === 'adolescent') await this.loadAdolescent();
      else if (this.tab === 'pnc') await this.loadPnc();
      else if (this.tab === 'pmtct') await this.loadPmtct();
    },
    blankPacForm() { return { patient_id: null, gestation_weeks: null, presentation: 'incomplete', severity: 'uncomplicated', bleeding: 'mild',
      method: 'MVA', temperature: null, bp_systolic: null, bp_diastolic: null, pulse: null, hb_level: null, complications: '',
      fp_method_offered: 1, fp_method_accepted: 0, fp_method_chosen: '', fp_started_same_visit: 0, fp_refusal_reason: '',
      sgbv_screened: 0, sgbv_concerns: 0, mental_health_screened: 0, epds_score: null, suicidal_ideation: 0, mh_referral_made: 0, counselling_received: 0 }; },
    blankCancerForm() { return { patient_id: null, screen_type: 'VIA', screen_date: new Date().toISOString().substring(0,10), result: 'pending', findings: '', treatment: 'none', next_screen_due: '', referral_facility: '', notes: '' }; },
    async loadRisk() {
      try {
        const payload = { function: 'getHighRiskPregnancies', category: this.riskFilter };
        if (this.riskPatientFilter) payload.patient_id = this.riskPatientFilter;
        const r = await executeJson(payload, hms);
        if (r.data && r.data.success) this.risk = r.data.data;
        const a = await executeJson({ function: 'getPregnancyRiskAlerts', resolved: '0' }, hms);
        if (a.data && a.data.success) this.alerts = a.data.data.Alerts || [];
      } catch (e) { console.error(e); }
    },
    clearRiskPatientFilter() { this.riskPatientFilter = ''; this.loadRisk(); },
    async recompute(visitId) {
      const r = await executeJson({ function: 'ancRecomputeRisk', anc_visit_id: visitId }, hms);
      if (r.data && r.data.success) await this.loadRisk();
    },
    async ackAlert(a) {
      const action = prompt('Action taken?'); if (action === null) return;
      const r = await executeJson({ function: 'acknowledgePregnancyRiskAlert', alert_id: a.alert_id, action_taken: action }, hms);
      if (r.data && r.data.success) await this.loadRisk();
    },
    async resolveAlert(a) {
      const outcomes = ['resolved','referred','adverse_event','maternal_death','perinatal_death'];
      const outcome = prompt('Outcome (' + outcomes.join(' / ') + '):'); if (!outcome) return;
      if (!outcomes.includes(outcome)) { alert('Bad outcome.'); return; }
      const r = await executeJson({ function: 'resolvePregnancyRiskAlert', alert_id: a.alert_id, resolution_outcome: outcome }, hms);
      if (r.data && r.data.success) await this.loadRisk();
    },

    async loadMpdsr() {
      try {
        const r = await executeJson({ function: 'getMpdsrCases' }, hms);
        if (r.data && r.data.success) { this.mpdsrCases = r.data.data.Cases || []; this.mpdsrStats = r.data.data.Stats || {}; }
      } catch (e) { console.error(e); }
    },
    async openCase(c) {
      const r = await executeJson({ function: 'getMpdsrCaseDetail', review_id: c.review_id }, hms);
      if (r.data && r.data.success) {
        this.activeCase = r.data.data;
        this.reviewForm = { ...r.data.data.Review };
      }
    },
    async notify() {
      const r = await executeJson({ function: 'notifyMpdsr', review_id: this.activeCase.Review.review_id }, hms);
      if (r.data && r.data.success) { alert(r.data.message); await this.openCase({ review_id: this.activeCase.Review.review_id }); }
    },
    async saveReview(complete) {
      this.saving = true;
      try {
        const payload = { function: 'saveMpdsrReview', review_id: this.activeCase.Review.review_id, mark_complete: complete, ...this.reviewForm };
        const r = await executeJson(payload, hms);
        if (r.data && r.data.success) { alert(r.data.message); await this.openCase({ review_id: this.activeCase.Review.review_id }); }
      } catch (e) { console.error(e); } finally { this.saving = false; }
    },
    openActionModal(a) {
      this.actionForm = a ? { ...a } : { category: 'clinical_protocol', description: '', assigned_to_dept: '', due_date: '', priority: 'high' };
      this.actionModal = true;
    },
    async saveAction() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'saveMpdsrAction', review_id: this.activeCase.Review.review_id, ...this.actionForm }, hms);
        if (r.data && r.data.success) { this.actionModal = false; await this.openCase({ review_id: this.activeCase.Review.review_id }); }
      } catch (e) { console.error(e); } finally { this.saving = false; }
    },
    async closeAction(a) {
      const ev = prompt('Closure evidence?'); if (ev === null) return;
      const r = await executeJson({ function: 'closeMpdsrAction', action_id: a.action_id, closure_evidence: ev }, hms);
      if (r.data && r.data.success) { if (r.data.data && r.data.data.review_auto_closed) alert('All actions closed — review auto-closed.'); await this.openCase({ review_id: this.activeCase.Review.review_id }); }
    },
    openVaModal() {
      this.vaForm = this.activeCase.VerbalAutopsy ? { ...this.activeCase.VerbalAutopsy } : {};
      this.vaModal = true;
    },
    async saveVA() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'saveMpdsrVerbalAutopsy', review_id: this.activeCase.Review.review_id, ...this.vaForm }, hms);
        if (r.data && r.data.success) { this.vaModal = false; await this.openCase({ review_id: this.activeCase.Review.review_id }); }
      } catch (e) { console.error(e); } finally { this.saving = false; }
    },

    // formatting
    riskRowClass(c) { return ({ critical:'row-action', high:'row-warn', moderate:'row-info' })[c] || ''; },
    riskPill(c) { return ({ critical:'hms-pill-danger', high:'hms-pill-warn', moderate:'hms-pill-info', low:'hms-pill-success' })[c] || 'hms-pill-muted'; },
    alertSeverityClass(s) { return 'hms-alert-' + s; },
    alertSeverityPill(s) { return ({ critical:'hms-pill-danger', urgent:'hms-pill-warn', warning:'hms-pill-info', info:'hms-pill-muted' })[s] || ''; },
    deathTypePill(t) { return ({ maternal:'hms-pill-danger', perinatal_stillbirth:'hms-pill-warn', perinatal_early_neonatal:'hms-pill-warn', near_miss:'hms-pill-info' })[t] || 'hms-pill-muted'; },
    mpdsrStatusPill(s) { return ({ overdue:'hms-pill-danger', pending_notification:'hms-pill-warn', notified:'hms-pill-info', under_review:'hms-pill-info', review_complete:'hms-pill-success', action_plan:'hms-pill-warn', closed:'hms-pill-muted' })[s] || ''; },
    mpdsrRowClass(c) { if (c.status === 'overdue' || c.hours_to_notify > 48) return 'row-action'; return ''; },
    actionStatusPill(s) { return ({ open:'hms-pill-warn', in_progress:'hms-pill-info', closed:'hms-pill-success', overdue:'hms-pill-danger', cancelled:'hms-pill-muted' })[s] || ''; },
    // EWS methods
    async recordBp() {
      this.saving = true;
      try {
        const payload = {
          function: 'recordBpMonitoring',
          ...this.bpForm,
          severe_features: this.bpForm.severe_features_list.join(',')
        };
        const r = await executeJson(payload, hms);
        if (r.data && r.data.success) {
          this.lastBpResult = r.data.data;
          this.bpForm = { patient_id: this.bpForm.patient_id, bp_systolic: null, bp_diastolic: null, pulse: null, proteinuria: 'nil', severe_features_list: [], convulsion: 0, notes: '' };
          await this.loadRisk();
        } else alert(r.data?.message || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },
    async startMgso4(result) {
      if (!confirm('Start MgSO4 protocol (Pritchard regimen)? Loading dose: 4g IV + 10g IM (5g each buttock).')) return;
      const r = await executeJson({ function: 'startMgso4Protocol', patient_id: this.bpForm.patient_id || this.lastBpResult.patient_id || 0, bp_id: result.bp_id, indication: result.classification === 'eclampsia' ? 'eclampsia' : 'severe_pre_eclampsia' }, hms);
      if (r.data && r.data.success) alert(r.data.message);
    },
    ewsResultCard(c) { return ({ eclampsia: 'hms-card-danger', severe_PE: 'hms-card-danger', mild_PE: 'hms-card-warn', gestational_HTN: 'hms-card-warn' })[c] || ''; },

    // PPH methods
    async loadPph() {
      const r = await executeJson({ function: 'getPphEvents' }, hms);
      if (r.data && r.data.success) { this.pphEvents = r.data.data.Events || []; this.pphStats = r.data.data.Stats || {}; }
    },
    openNewPphModal() {
      this.newPphForm = { delivery_id: null, initial_loss_ml: 0, timing: 'immediate_pph', suspected_cause: 'tone', cause_detail: '' };
      this.newPphModal = true;
    },
    async openNewPph() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'openPphEvent', ...this.newPphForm }, hms);
        if (r.data && r.data.success) {
          this.newPphModal = false;
          await this.loadPph();
          alert('PPH event opened. Recommended next actions:\n\n• ' + (r.data.data.recommended_actions || []).join('\n• '));
        } else alert(r.data?.message || 'Failed.');
      } catch (e) { console.error(e); } finally { this.saving = false; }
    },
    async openPphDetail(e) {
      const r = await executeJson({ function: 'getPphEventDetail', pph_id: e.pph_id }, hms);
      if (r.data && r.data.success) {
        this.activePph = r.data.data;
        this.interventionsList = (this.activePph.Event.interventions || '').split(',').filter(x => x);
        this.teamList = (this.activePph.Event.team_called || '').split(',').filter(x => x);
        this.bloodForm = { units_blood: this.activePph.Event.units_blood || 0, units_ffp: this.activePph.Event.units_ffp || 0, units_platelets: this.activePph.Event.units_platelets || 0 };
        this.closeForm = { status: 'controlled', outcome: 'survived', final_blood_loss_ml: this.activePph.Event.cumulative_loss_ml || 0, final_hb_level: null, notes: '' };
        this.lossForm = { added_loss_ml: null, bp_systolic: null, bp_diastolic: null, pulse: null, intervention_just_done: '' };
      }
    },
    async addPphLoss() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'addPphLoss', pph_id: this.activePph.Event.pph_id, ...this.lossForm }, hms);
        if (r.data && r.data.success) {
          if (r.data.data.mtp_just_triggered) alert('🚨 MTP ACTIVATED — call blood bank for emergency cross-match. Notify OB/GYN, anaesthetist, blood bank, ICU.');
          if (r.data.data.threshold_crossed === 'critical_1500') alert('🚨 1500 mL reached — consider surgical escalation (B-Lynch, balloon, hysterectomy).');
          this.lossForm = { added_loss_ml: null, bp_systolic: null, bp_diastolic: null, pulse: null, intervention_just_done: '' };
          await this.openPphDetail({ pph_id: this.activePph.Event.pph_id });
        }
      } catch (e) { console.error(e); } finally { this.saving = false; }
    },
    async saveInterventions() {
      this.saving = true;
      try {
        await executeJson({
          function: 'updatePphInterventions',
          pph_id: this.activePph.Event.pph_id,
          interventions: this.interventionsList.join(','),
          team_called: this.teamList.join(','),
          ...this.bloodForm
        }, hms);
        await this.openPphDetail({ pph_id: this.activePph.Event.pph_id });
        alert('Saved.');
      } catch (e) { console.error(e); } finally { this.saving = false; }
    },
    async closePph() {
      if (!confirm('Close this PPH event? Cannot be edited after.')) return;
      this.saving = true;
      try {
        await executeJson({ function: 'closePphEvent', pph_id: this.activePph.Event.pph_id, ...this.closeForm }, hms);
        this.activePph = null;
        await this.loadPph();
      } catch (e) { console.error(e); } finally { this.saving = false; }
    },
    formatIntervention(i) { return i.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()); },
    lossClass(ml) { if (ml >= 1500) return 'pt-bad'; if (ml >= 1000) return 'pt-bad'; if (ml >= 500) return 'pt-warn'; return ''; },
    pphStatusPill(s) { return ({ active:'hms-pill-danger', stabilising:'hms-pill-warn', controlled:'hms-pill-success', referred_out:'hms-pill-info', maternal_death:'hms-pill-danger' })[s] || ''; },

    // PAC methods
    async loadPac() {
      const r = await executeJson({ function: 'getPacCases' }, hms);
      if (r.data && r.data.success) { this.pacCases = r.data.data.Cases || []; this.pacStats = r.data.data.Stats || {}; }
    },
    openPacModal(c) { this.pacForm = c ? { ...this.blankPacForm(), ...c } : this.blankPacForm(); this.pacModal = true; },
    async savePac() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'savePacCase', ...this.pacForm }, hms);
        if (r.data && r.data.success) { this.pacModal = false; await this.loadPac(); }
        else alert(r.data?.message || 'Failed.');
      } catch (e) { console.error(e); } finally { this.saving = false; }
    },
    async dischargePac(c) {
      const fu = prompt('Follow-up date (YYYY-MM-DD)? Default = 14 days:');
      if (fu === null) return;
      try {
        const r = await executeJson({ function: 'dischargePacCase', pac_id: c.pac_id, follow_up_date: fu, outcome: 'discharged_well' }, hms);
        if (r.data && r.data.success) { alert(r.data.message); await this.loadPac(); }
        else alert(r.data?.message || 'Failed.');
      } catch (e) { console.error(e); }
    },
    async completeFollowup(c) {
      const outcome = prompt('Follow-up outcome (e.g. "Doing well, FP continued"):');
      if (outcome === null) return;
      const r = await executeJson({ function: 'markPacFollowupComplete', pac_id: c.pac_id, follow_up_outcome: outcome }, hms);
      if (r.data && r.data.success) await this.loadPac();
    },
    pacRowClass(c) { if (c.outcome === 'died') return 'row-action'; if (c.severity === 'life_threatening' || c.severity === 'severe') return 'row-warn'; return ''; },
    presentationPill(p) { return ({ induced_unsafe:'hms-pill-danger', septic:'hms-pill-danger', incomplete:'hms-pill-warn', inevitable:'hms-pill-warn', missed:'hms-pill-info', threatened:'hms-pill-info', complete:'hms-pill-success' })[p] || ''; },
    severityPill(s) { return ({ life_threatening:'hms-pill-danger', severe:'hms-pill-danger', moderate:'hms-pill-warn', mild:'hms-pill-info', uncomplicated:'hms-pill-success' })[s] || ''; },
    pacOutcomePill(o) { return ({ died:'hms-pill-danger', complications:'hms-pill-warn', referred:'hms-pill-info', discharged_well:'hms-pill-success' })[o] || 'hms-pill-muted'; },

    // Cancer methods
    async loadCancer() {
      const r = await executeJson({ function: 'getCancerScreenings' }, hms);
      if (r.data && r.data.success) {
        this.cancerScreenings = r.data.data.Screenings || [];
        this.cancerStats = r.data.data.Stats || {};
        this.cancerDue = r.data.data.DueRecalls || [];
      }
    },
    openCancerModal(s) { this.cancerForm = s ? { ...this.blankCancerForm(), ...s } : this.blankCancerForm(); this.cancerModal = true; },
    async saveCancerScreen() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'saveCancerScreening', ...this.cancerForm }, hms);
        if (r.data && r.data.success) { this.cancerModal = false; await this.loadCancer(); }
      } catch (e) { console.error(e); } finally { this.saving = false; }
    },
    async rebuildSchedule() {
      if (!confirm('Rebuild screening schedule for all women in the facility?')) return;
      const r = await executeJson({ function: 'rebuildCancerSchedule' }, hms);
      if (r.data && r.data.success) { alert(r.data.message); await this.loadCancer(); }
    },
    screenTypePill(t) { return ({ VIA:'hms-pill-info', VILI:'hms-pill-info', HPV:'hms-pill-info', Pap:'hms-pill-info', SBE:'hms-pill-warn', CBE:'hms-pill-warn', mammography:'hms-pill-warn', colposcopy:'hms-pill-warn' })[t] || ''; },
    cancerResultPill(r) { return ({ negative:'hms-pill-success', pending:'hms-pill-muted', positive_low_grade:'hms-pill-warn', positive_high_grade:'hms-pill-danger', suspicious_cancer:'hms-pill-danger', inconclusive:'hms-pill-warn', referred:'hms-pill-info' })[r] || ''; },
    cancerResultRow(r) { if (r === 'positive_high_grade' || r === 'suspicious_cancer') return 'row-action'; if (r === 'positive_low_grade') return 'row-warn'; return ''; },

    formatDate(d) { if (!d) return ''; const dt = new Date((d + '').replace(' ','T')); return isNaN(dt) ? d : dt.toLocaleDateString(); },
    formatDt(d) { if (!d) return ''; const dt = new Date((d + '').replace(' ','T')); return isNaN(dt) ? d : dt.toLocaleString(); },
    formatDateTime(d) { return this.formatDt(d); },

    // ----- Adolescent register -----
    blankAdolForm() { return { apr_id: null, patient_id: null, booking_date: new Date().toISOString().substring(0,10),
      age_at_booking: null, gestation_at_booking: null, anc_visit_id: null,
      pregnancy_circumstances: 'unclear', partner_age: null,
      in_school_before_pregnancy: 0, school_name: '', school_level: '', school_form_class: '',
      parental_support_level: 'unknown', primary_caregiver: '', primary_caregiver_phone: '',
      counselling_offered: 0, counselling_attended: 0, legal_referral_needed: 0, legal_referral_made: 0,
      chv_assigned: 0, teen_friendly_clinic_assigned: 0, notes: '',
      pregnancy_outcome: 'ongoing', outcome_date: '', school_reentry_status: 'unknown', school_reentry_date: '',
      fp_post_delivery_offered: 0, fp_post_delivery_accepted: 0, fp_post_delivery_method: ''
    }; },
    async loadAdolescent() {
      try {
        const r = await executeJson({ function: 'getAdolescentRegister', outcome_status: this.adolFilter }, hms);
        if (r.data && r.data.success) { this.adolRows = r.data.data.rows || []; this.adolStats = r.data.data.stats || {}; }
      } catch (e) { console.error(e); }
    },
    openAdolModal() { this.adolForm = this.blankAdolForm(); this.adolModal = true; },
    editAdol(a) { this.adolForm = { ...a }; this.adolModal = true; },
    async saveAdol() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'saveAdolescentRegister', ...this.adolForm }, hms);
        if (r.data && r.data.success) {
          if (r.data.data && r.data.data.statutory_concern) alert('STATUTORY CONCERN flagged — legal referral required under Kenya Sexual Offences Act.');
          this.adolModal = false;
          await this.loadAdolescent();
        } else if (r.data) alert(r.data.message || 'Save failed.');
      } finally { this.saving = false; }
    },
    async markAdolFu(a, which) {
      const status = prompt(which === 'one_year' ? '1-year follow-up status (in school / on FP / lost / etc.):' : '2-year follow-up status:');
      if (!status) return;
      const r = await executeJson({ function: 'markAdolescentFollowupDone', apr_id: a.apr_id, which, status }, hms);
      if (r.data && r.data.success) await this.loadAdolescent();
    },
    adolYearDue(outcomeDate) { if (!outcomeDate) return ''; const d = new Date((outcomeDate + '').replace(' ','T')); d.setFullYear(d.getFullYear() + 1); return d.toISOString().substring(0,10); },
    adolRowClass(a) { if (a.partner_statutory_concern == 1 || a.age_at_booking < 16) return 'row-action'; if (a.pregnancy_circumstances === 'sgbv') return 'row-action'; if (a.school_reentry_status === 'dropped_out') return 'row-warn'; return ''; },
    adolCircClass(c) { return ({ sgbv:'hms-pill-danger', statutory_concern:'hms-pill-danger', unclear:'hms-pill-muted', declined_to_disclose:'hms-pill-warn', consensual_peer:'hms-pill-info', consensual_age_appropriate:'hms-pill-success' })[c] || 'hms-pill-muted'; },
    adolSchoolClass(s) { return ({ returned:'hms-pill-success', enrolled_alternative:'hms-pill-success', planning:'hms-pill-info', dropped_out:'hms-pill-danger', not_applicable:'hms-pill-muted', unknown:'hms-pill-muted' })[s] || 'hms-pill-muted'; },
    adolOutcomeClass(o) { return ({ ongoing:'hms-pill-info', delivered:'hms-pill-success', miscarriage:'hms-pill-warn', induced_abortion:'hms-pill-warn', stillbirth:'hms-pill-danger', transferred:'hms-pill-muted', LTFU:'hms-pill-danger' })[o] || 'hms-pill-muted'; },

    // ----- PNC -----
    blankPncForm() { return { pnc_id: null, visit_timing: '6hr', visit_date: new Date().toISOString().substring(0,16), location: 'facility',
      bp_systolic: null, bp_diastolic: null, pulse: null, temperature: null,
      uterine_involution: 'normal', lochia: 'normal', perineum_healing: 'normal', breast_problems: 'none',
      epds_score: null, epds_q10_self_harm: 0, suicidal_ideation: 0,
      baby_examined: 1, baby_weight_g: null, baby_temperature: null, baby_feeding: 'exclusive_bf',
      baby_jaundice: 0, baby_cord_normal: 1, baby_immunizations_given: '',
      fp_discussed: 0, fp_method_chosen: '', fp_started_today: 0,
      referral_made: 0, referral_reason: '', notes: ''
    }; },
    async loadPnc() {
      try {
        const r = await executeJson({ function: 'getPncSchedule', status: this.pncFilter }, hms);
        if (r.data && r.data.success) { this.pncRows = r.data.data.rows || []; this.pncStats = r.data.data.stats || {}; }
      } catch (e) { console.error(e); }
    },
    async autoSchedule() {
      if (!this.pncScheduleId) return;
      const r = await executeJson({ function: 'autoSchedulePnc', delivery_id: this.pncScheduleId }, hms);
      if (r.data && r.data.success) { this.pncScheduleId = null; await this.loadPnc(); }
      else if (r.data) alert(r.data.message || 'Schedule failed.');
    },
    openPncVisit(v) {
      this.pncForm = { ...this.blankPncForm(), ...v };
      // Default visit_date to now if not yet completed
      if (!v.visit_date) this.pncForm.visit_date = new Date().toISOString().substring(0,16);
      else this.pncForm.visit_date = (v.visit_date + '').replace(' ','T').substring(0,16);
      // Split SET values back into arrays
      this.pncMaternalDangerArr = (v.danger_signs_found && v.danger_signs_found !== 'none') ? v.danger_signs_found.split(',').map(s => s.trim()).filter(s => s && s !== 'none') : [];
      this.pncBabyDangerArr = (v.baby_danger_signs && v.baby_danger_signs !== 'none') ? v.baby_danger_signs.split(',').map(s => s.trim()).filter(s => s && s !== 'none') : [];
      this.pncModal = true;
    },
    async savePnc() {
      this.saving = true;
      try {
        const payload = { function: 'savePncVisit', ...this.pncForm,
          danger_signs_found: this.pncMaternalDangerArr.length ? this.pncMaternalDangerArr.join(',') : 'none',
          baby_danger_signs: this.pncBabyDangerArr.length ? this.pncBabyDangerArr.join(',') : 'none',
          danger_signs_screened: 1, mental_health_screened: this.pncIs6w ? 1 : (this.pncForm.epds_score ? 1 : 0)
        };
        const r = await executeJson(payload, hms);
        if (r.data && r.data.success) {
          if (r.data.data && r.data.data.alerts && r.data.data.alerts.length) {
            alert('Saved — ' + r.data.data.alerts.length + ' clinical alert(s) opened. Check the High-Risk tab.');
          }
          this.pncModal = false;
          await this.loadPnc();
        } else if (r.data) alert(r.data.message || 'Save failed.');
      } finally { this.saving = false; }
    },
    async markMissed(v) {
      if (!confirm('Mark this PNC visit as missed?')) return;
      const r = await executeJson({ function: 'markPncMissed', pnc_id: v.pnc_id }, hms);
      if (r.data && r.data.success) await this.loadPnc();
    },
    pncTimingLabel(t) { return ({ '6hr':'6 hours', '24hr':'24 hours', '48hr':'48 hours', '1wk':'1 week', '6wk':'6 weeks' })[t] || t; },
    pncStatusClass(s) { return ({ scheduled:'hms-pill-info', completed:'hms-pill-success', missed:'hms-pill-danger', declined:'hms-pill-warn', transferred:'hms-pill-muted' })[s] || ''; },
    pncRowClass(v) {
      if (v.epds_q10_self_harm == 1 || v.suicidal_ideation == 1) return 'row-action';
      if (v.danger_signs_found && v.danger_signs_found !== 'none' && v.danger_signs_found !== '') return 'row-action';
      if (v.visit_status === 'scheduled' && v.scheduled_date && new Date(v.scheduled_date.replace(' ','T')) < new Date()) return 'row-warn';
      if (v.visit_status === 'missed') return 'row-warn';
      return '';
    },
    epdsPill(score, q10) {
      if (q10 == 1) return 'hms-pill-danger';
      const s = parseInt(score, 10);
      if (s >= 13) return 'hms-pill-danger';
      if (s >= 10) return 'hms-pill-warn';
      return 'hms-pill-success';
    },

    // ----- PMTCT -----
    blankPmtctForm() { return { patient_id: null, anc_visit_id: null, hiv_status: 'positive',
      test_date: new Date().toISOString().substring(0,10), test_type: 'determine', diagnosis_setting: 'this_pregnancy',
      baseline_cd4: null, cd4_date: '', who_clinical_stage: '', baseline_vl_copies: null,
      tb_screened: 0, tb_screen_result: '',
      art_started: 0, art_start_date: '', art_regimen: 'TDF/3TC/DTG (TLD)', art_line: 'first_line',
      cotrimoxazole_prophylaxis: 1,
      partner_tested: 0, partner_status: 'unknown', partner_on_art: 0,
      notes: ''
    }; },
    pct(n, d) { const num = Number(n) || 0; const den = Number(d) || 0; if (den === 0) return ''; return Math.round((num / den) * 100) + '%'; },
    async loadPmtct() {
      try {
        const r = await executeJson({ function: 'getPmtctCascade' }, hms);
        if (r.data && r.data.success) this.pmtctCascade = r.data.data || { funnel: {}, vl_overdue: 0, dbs_overdue: 0 };
        if (this.pmtctView === 'mothers') await this.loadPmtctMothers();
        else await this.loadHei();
      } catch (e) { console.error(e); }
    },
    async loadPmtctMothers() {
      try {
        const r = await executeJson({ function: 'getPmtctClients', cascade_stage: this.pmtctStageFilter }, hms);
        if (r.data && r.data.success) this.pmtctMothers = r.data.data.rows || [];
      } catch (e) { console.error(e); }
    },
    async loadHei() {
      try {
        const r = await executeJson({ function: 'getHeiRecords', final_status: this.heiStatusFilter }, hms);
        if (r.data && r.data.success) this.heiRows = r.data.data.rows || [];
      } catch (e) { console.error(e); }
    },
    openPmtctEnrollModal() { this.pmtctForm = this.blankPmtctForm(); this.pmtctEnrollModal = true; },
    async savePmtctEnrol() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'enrollPmtctClient', ...this.pmtctForm }, hms);
        if (r.data && r.data.success) {
          this.pmtctEnrollModal = false;
          await this.loadPmtct();
        } else if (r.data) alert(r.data.message || 'Save failed.');
      } finally { this.saving = false; }
    },
    async openPmtctDetail(m) {
      this.activePmtct = m;
      const r = await executeJson({ function: 'getPmtctVlSchedule', pmtct_id: m.pmtct_id }, hms);
      if (r.data && r.data.success) this.vlSchedule = r.data.data.rows || [];
      this.vlModal = true;
      this.vlEntry = {};
    },
    openVlEntry(v) {
      this.vlEntry = { vl_id: v.vl_id, sample_taken_date: new Date().toISOString().substring(0,10),
        result_date: new Date().toISOString().substring(0,10), vl_copies: null, action_taken: '',
        enhanced_adherence_counselling: 0, regimen_switch_made: 0 };
    },
    async saveVlEntry() {
      const r = await executeJson({ function: 'recordPmtctViralLoad', ...this.vlEntry }, hms);
      if (r.data && r.data.success) {
        if (r.data.data && r.data.data.alert_id) alert('VL recorded — critical alert opened for high VL.');
        this.vlEntry = {};
        await this.openPmtctDetail(this.activePmtct);
        await this.loadPmtct();
      } else if (r.data) alert(r.data.message || 'Save failed.');
    },
    async openHeiDetail(h) {
      this.activeHei = h;
      const r = await executeJson({ function: 'getHeiDetail', hei_id: h.hei_id }, hms);
      if (r.data && r.data.success) { this.heiTests = r.data.data.tests || []; }
      this.heiModal = true;
      this.dbsEntry = {};
    },
    openDbsEntry(t) {
      this.dbsEntry = { dbs_id: t.dbs_id, sample_taken_date: new Date().toISOString().substring(0,10),
        result: 'pending', result_date: '', result_disclosed_to_mother: 0, notes: '' };
    },
    async saveDbsEntry() {
      const r = await executeJson({ function: 'recordDbsTest', ...this.dbsEntry }, hms);
      if (r.data && r.data.success) {
        this.dbsEntry = {};
        await this.openHeiDetail(this.activeHei);
        await this.loadPmtct();
      } else if (r.data) alert(r.data.message || 'Save failed.');
    },
    pmtctRowClass(m) {
      if (m.cascade_stage === 'infant_positive') return 'row-action';
      if (m.cascade_stage === 'LTFU') return 'row-action';
      if (m.cascade_stage === 'enrolled' && !m.art_started) return 'row-action';
      if (m.latest_vl && m.latest_vl >= 1000) return 'row-warn';
      return '';
    },
    heiRowClass(h) {
      if (h.final_status === 'positive') return 'row-action';
      if (h.final_status === 'LTFU' || h.final_status === 'died') return 'row-action';
      if (h.final_status === 'final_negative') return '';
      return '';
    },
    stagePill(s) { return ({ enrolled:'hms-pill-warn', art_started:'hms-pill-info', vl_done:'hms-pill-info', suppressed:'hms-pill-success', delivered:'hms-pill-info', infant_enrolled:'hms-pill-info', infant_dbs1:'hms-pill-info', infant_final_negative:'hms-pill-success', infant_positive:'hms-pill-danger', LTFU:'hms-pill-danger', transferred:'hms-pill-muted' })[s] || 'hms-pill-muted'; },
    partnerPill(s) { return ({ positive:'hms-pill-info', negative:'hms-pill-success', declined:'hms-pill-warn', unknown:'hms-pill-muted', not_disclosed:'hms-pill-warn' })[s] || 'hms-pill-muted'; },
    vlPill(copies) { const c = Number(copies); if (c <= 50) return 'hms-pill-success'; if (c < 1000) return 'hms-pill-success'; if (c < 10000) return 'hms-pill-warn'; return 'hms-pill-danger'; },
    vlStatusPill(s) { return ({ pending:'hms-pill-muted', suppressed:'hms-pill-success', LDL:'hms-pill-success', low_level:'hms-pill-warn', high_level:'hms-pill-danger', rejected:'hms-pill-warn' })[s] || 'hms-pill-muted'; },
    heiStatusPill(s) { return ({ on_followup:'hms-pill-info', final_negative:'hms-pill-success', positive:'hms-pill-danger', LTFU:'hms-pill-danger', transferred:'hms-pill-muted', died:'hms-pill-danger' })[s] || 'hms-pill-muted'; },
    dbsResultPill(r) { return ({ pending:'hms-pill-muted', negative:'hms-pill-success', positive:'hms-pill-danger', rejected:'hms-pill-warn', indeterminate:'hms-pill-warn' })[r] || 'hms-pill-muted'; }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-page-title { font-size: 18px; font-weight: 600; color: #2d3748; }

.hms-tab-row { display: flex; gap: 4px; border-bottom: 1px solid #e2e8f0; margin-bottom: 14px; }
.hms-tab { padding: 10px 16px; border: none; background: none; cursor: pointer; color: #718096; border-bottom: 2px solid transparent; font-size: 14px; }
.hms-tab.active { color: #2b6cb0; border-bottom-color: #2b6cb0; font-weight: 600; }

.hms-stat-row { display: grid; grid-template-columns: repeat(6, 1fr); gap: 10px; margin-bottom: 14px; }
.hms-stat { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; text-align: center; border-top: 4px solid #94a3b8; }
.hms-stat-critical { border-top-color: #dc2626; background: #fef2f2; }
.hms-stat-high     { border-top-color: #ea580c; background: #fff7ed; }
.hms-stat-moderate { border-top-color: #d97706; background: #fffbeb; }
.hms-stat-danger   { border-top-color: #dc2626; }
.hms-stat-warning  { border-top-color: #ea580c; }
.hms-stat-success  { border-top-color: #16a34a; }
.hms-stat-num { font-size: 22px; font-weight: 700; color: #1a202c; }
.hms-stat-lbl { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .4px; margin-top: 2px; }

.hms-table { width: 100%; border-collapse: collapse; font-size: 13px; background: #fff; }
.hms-table th { background: #f7fafc; padding: 9px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #4a5568; text-align: left; }
.hms-table td { padding: 8px 9px; border-bottom: 1px solid #edf2f7; vertical-align: middle; }
.hms-table tr.row-action { background: #fff5f5; }
.hms-table tr.row-warn   { background: #fffaf0; }
.hms-table tr.row-info   { background: #f0f9ff; }

.hms-input { width: 100%; padding: 6px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-label { font-size: 11px; font-weight: 600; color: #4a5568; display: block; margin-bottom: 3px; }
.hms-section-lbl { font-size: 11px; font-weight: 700; color: #718096; text-transform: uppercase; letter-spacing: .5px; padding-bottom: 3px; border-bottom: 1px solid #e2e8f0; }

.hms-pill { display: inline-flex; align-items: center; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.hms-pill-success { background: #c6f6d5; color: #22543d; }
.hms-pill-info    { background: #bee3f8; color: #2a4365; }
.hms-pill-warn    { background: #feebc8; color: #7b341e; }
.hms-pill-danger  { background: #fed7d7; color: #742a2a; }
.hms-pill-muted   { background: #edf2f7; color: #4a5568; }

.hms-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; }
.hms-card-head { padding: 10px 14px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #2d3748; font-size: 13px; }
.hms-card-body { padding: 14px; }
.hms-card-body.p-0 { padding: 0; }

.hms-h3 { font-size: 16px; font-weight: 600; color: #2d3748; margin: 0; }
.hms-h4 { font-size: 14px; font-weight: 600; color: #2d3748; margin: 10px 0; }

.hms-alert-row { background: #fff; border: 1px solid #e2e8f0; border-left: 4px solid #94a3b8; border-radius: 6px; padding: 10px 14px; margin-bottom: 8px; display: flex; justify-content: space-between; align-items: center; gap: 12px; }
.hms-alert-row.hms-alert-critical { border-left-color: #dc2626; background: #fef2f2; }
.hms-alert-row.hms-alert-urgent   { border-left-color: #ea580c; background: #fff7ed; }
.hms-alert-row.hms-alert-warning  { border-left-color: #d97706; background: #fffbeb; }

.hms-banner { padding: 12px 16px; border-radius: 6px; margin-bottom: 14px; font-size: 13px; display: flex; align-items: center; justify-content: space-between; }
.hms-banner-danger { background: #fef2f2; border: 1px solid #fca5a5; color: #991b1b; }

.hms-cascade { display: flex; align-items: center; gap: 4px; background: #fff; padding: 14px; border-radius: 8px; border: 1px solid #e2e8f0; margin-bottom: 12px; overflow-x: auto; }
.hms-cascade-step { flex: 1; min-width: 110px; text-align: center; padding: 8px 10px; border-radius: 6px; background: #f7fafc; }
.hms-cascade-num { font-size: 22px; font-weight: 700; color: #2d3748; line-height: 1; }
.hms-cascade-lbl { font-size: 11px; color: #4a5568; margin-top: 4px; font-weight: 600; text-transform: uppercase; letter-spacing: .3px; }
.hms-cascade-pct { font-size: 11px; color: #2b6cb0; margin-top: 2px; font-weight: 600; }
.hms-cascade-pct-good { color: #22543d; }
.hms-cascade-arrow { color: #a0aec0; font-size: 22px; }

.hms-confidential-banner { background: #fef2f2; border-left: 4px solid #b91c1c; color: #7f1d1d; padding: 10px 14px; border-radius: 6px; margin-bottom: 12px; font-size: 13px; display: flex; align-items: center; gap: 8px; }
.hms-confidential-banner .mdi { font-size: 18px; }

.hms-chip-row { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px; }
.hms-chip { display: inline-flex; align-items: center; gap: 4px; background: #f7fafc; border: 1px solid #e2e8f0; padding: 4px 10px; border-radius: 14px; font-size: 12px; cursor: pointer; user-select: none; }
.hms-chip input { margin: 0; }
.hms-chip:has(input:checked) { background: #fed7d7; border-color: #fc8181; color: #742a2a; }

.hms-check { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; }

.hms-mpdsr-grid { display: grid; grid-template-columns: 1.4fr 1fr; gap: 14px; }
@media (max-width: 1000px) { .hms-mpdsr-grid { grid-template-columns: 1fr; } }

.hms-delay-block { background: #f8fafc; padding: 10px 12px; border-radius: 6px; margin-bottom: 8px; }
.hms-delay-block label { font-size: 13px; }

.hms-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.55); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px; }
.hms-modal { background: #fff; border-radius: 8px; max-height: 92vh; display: flex; flex-direction: column; overflow: hidden; }
.hms-modal-head { padding: 14px 18px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.hms-modal-body { padding: 16px 18px; overflow-y: auto; }
.hms-modal-foot { padding: 12px 18px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; }
.hms-icon-btn { background: none; border: none; cursor: pointer; }

.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-sm { padding: 4px 10px; font-size: 12px; }
.hms-btn-blue      { background: #2b6cb0; color: #fff; }
.hms-btn-success   { background: #38a169; color: #fff; }
.hms-btn-secondary { background: #fff; color: #2d3748; border: 1px solid #cbd5e0; }
.hms-btn-danger    { background: #dc2626; color: #fff; }

.hms-muted { color: #a0aec0; }
.hms-muted-sm { color: #a0aec0; font-size: 11px; }
.hms-empty { padding: 24px; text-align: center; color: #a0aec0; }
.hms-severe-flags { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; background: #f7fafc; padding: 10px; border-radius: 6px; }
.hms-severe-flags label { font-size: 12px; }
.hms-card-danger { border: 2px solid #dc2626; background: linear-gradient(135deg, #fff 0%, #fef2f2 100%); }
.hms-card-warn   { border: 2px solid #d97706; background: linear-gradient(135deg, #fff 0%, #fffbeb 100%); }
.hms-btn-warning { background: #d97706; color: #fff; }
.hms-btn-lg { padding: 10px 18px; font-size: 14px; }

/* PPH detail */
.hms-pph-detail { background: #fafafa; padding: 16px; border-radius: 8px; }
.hms-pph-banner { display: flex; align-items: center; gap: 18px; padding: 16px 20px; border-radius: 8px; margin-bottom: 14px; background: #fef2f2; border: 2px solid #fca5a5; }
.hms-pph-active     { animation: hms-pph-pulse 2s infinite; }
@keyframes hms-pph-pulse { 0%,100% { box-shadow: 0 0 0 0 rgba(220,38,38,.4); } 50% { box-shadow: 0 0 0 8px rgba(220,38,38,0); } }
.hms-pph-loss { text-align: center; }
.hms-pph-loss-num { font-size: 38px; font-weight: 800; color: #991b1b; }
.hms-pph-loss-lbl { font-size: 11px; color: #991b1b; text-transform: uppercase; }
.hms-pph-banner-msg { color: #991b1b; }
.hms-pph-amtsl-warn { margin-left: auto; background: #fef3c7; padding: 8px 12px; border-radius: 6px; color: #78350f; }
.hms-pph-grid { display: grid; grid-template-columns: 1.2fr 1fr; gap: 14px; }
@media (max-width: 1000px) { .hms-pph-grid { grid-template-columns: 1fr; } }
.hms-checks { display: flex; flex-direction: column; gap: 4px; font-size: 12px; }
.hms-checks label { padding: 3px 0; }
.pt-bad { color: #c53030; font-weight: 700; }
.pt-warn { color: #d97706; font-weight: 600; }

.hms-pac-notice { background: #f5f3ff; border-left: 4px solid #7c3aed; padding: 12px 16px; border-radius: 6px; display: flex; gap: 12px; align-items: flex-start; margin-bottom: 14px; font-size: 13px; color: #4c1d95; }
.hms-pac-notice i { color: #7c3aed; font-size: 22px; flex-shrink: 0; }
.hms-pad { padding: 24px; }
</style>
